import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  ArrowLeft,
  RefreshCw,
  Plus,
  Wallet,
  Receipt,
  Shield,
  FileText,
  Pill,
  FileDown,
  CalendarIcon,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  Copy,
  Lock,
} from 'lucide-react';
import { format } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  useCanMutateFrontDeskIpd,
  useFrontDeskBasePath,
  useIsFrontDeskStaff,
  useIsReceptionistOnly,
} from '@/hooks/useFrontDeskBasePath';
import { ipdApi } from '@/api/ipd';
import { ipdBillingApi, type IPDBillTrack } from '@/api/ipdBilling';
import { labApi } from '@/api/lab';
import { ensureDoctorPrefix } from '@/lib/doctorName';
import { MEDICAL_SPECIALISATIONS } from '@/lib/medicalSpecialisations';
import { istDatetimeLocalToIso, toIstDatetimeLocalValue } from '@/utils/dateUtils';
import {
  isLabIncludedInPackage,
  isMedicineIncludedInPackage,
  isPackagePatient,
  packageTypeLabel,
  resolvePackageType,
} from '@/lib/ipdPackageTypes';
import AdmissionPackageEditor from '@/components/ipd/AdmissionPackageEditor';
import IPDLoyaltyBillingPanel from '@/components/billing/IPDLoyaltyBillingPanel';
import type {
  IPDBillItem,
  IPDBillStatus,
  PaymentMethod,
  IPDRefund,
  IPDDeposit,
  IPDDepositPurpose,
  IPDChargeSourceType,
  IPDChargeableItem,
  IPDPendingCollection,
} from '@/types/ipdBilling';
import type { LabTest } from '@/types/types';

const PAYMENT_METHODS: PaymentMethod[] = [
  'CASH',
  'CARD',
  'UPI',
  'BANK_TRANSFER',
  'CHEQUE',
  'OTHER',
];
const DEPOSIT_PURPOSES: IPDDepositPurpose[] = ['ADVANCE', 'TOP_UP', 'EMERGENCY', 'FULL_AND_FINAL'];
const BILL_STATUSES: IPDBillStatus[] = ['DRAFT', 'REVIEWED', 'APPROVED', 'FROZEN'];
const PHARMACY_DISPLAY_MODES: { value: 'DETAILED' | 'SUMMARY' | 'HIDDEN_IN_PACKAGE'; label: string }[] = [
  { value: 'DETAILED', label: 'Detailed (Itemized)' },
  { value: 'SUMMARY', label: 'Summary (Total only)' },
  { value: 'HIDDEN_IN_PACKAGE', label: 'Included in Package (Hidden)' },
];

// Row type for unified Add Items dialog: PHARMA/CONSUMABLE/MISC/LAB sub-types
type BuiltInChargeableRowType =
  | 'PHARMA'
  | 'CONSUMABLE'
  | 'LAB_TEST'
  | 'CONSULTANT_VISIT'
  | 'ANESTHETIST'
  | 'SURGEON_FEES'
  | 'OTHER';
type ChargeableRowType = BuiltInChargeableRowType | `CUSTOM_${string}`;

const CHARGEABLE_ROW_TYPES: { value: BuiltInChargeableRowType; label: string }[] = [
  { value: 'PHARMA', label: 'Pharmacy' },
  { value: 'CONSUMABLE', label: 'Consumable' },
  { value: 'LAB_TEST', label: 'Lab tests' },
  { value: 'CONSULTANT_VISIT', label: 'Consultant visit' },
  { value: 'ANESTHETIST', label: 'Anesthetist charges' },
  { value: 'SURGEON_FEES', label: 'Surgeon fees' },
  { value: 'OTHER', label: 'Other (misc)' },
];
function rowTypeToSourceAndCode(type: ChargeableRowType): { sourceType: IPDChargeSourceType; chargeCode: string } {
  if (type === 'PHARMA' || type === 'CONSUMABLE') return { sourceType: type, chargeCode: '' };
  if (type === 'LAB_TEST') return { sourceType: 'MISC', chargeCode: 'LAB_TEST' };
  return { sourceType: 'MISC', chargeCode: type };
}

function filterLabTestsForSearch(tests: LabTest[], query: string): LabTest[] {
  const q = query.trim().toLowerCase();
  if (!q) return tests;
  const tokens = q.split(/\s+/).filter(Boolean);
  return tests.filter((test) => {
    const hay = [test.name, test.code, test.description, test.sampleType]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return tokens.every((token) => hay.includes(token));
  });
}

function formatDate(s: string | undefined) {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCurrency(n: number) {
  return `₹${Number(n).toFixed(2)}`;
}

export default function IPDBillingPage() {
  const { admissionId } = useParams<{ admissionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isFrontDesk = useIsFrontDeskStaff();
  const canMutateIpd = useCanMutateFrontDeskIpd();
  const isReceptionistOnly = useIsReceptionistOnly();
  const basePath = useFrontDeskBasePath();
  const isOfflineBilling =
    location.pathname.includes('/offline-billing') ||
    new URLSearchParams(location.search).get('billTrack') === 'OFFLINE';
  const billTrack: IPDBillTrack = isOfflineBilling ? 'OFFLINE' : 'REVENUE';
  const [activeTab, setActiveTab] = useState('bill');
  const [depositOpen, setDepositOpen] = useState(false);
  const [editingDeposit, setEditingDeposit] = useState<IPDDeposit | null>(null);
  const [depositForm, setDepositForm] = useState({
    purpose: 'ADVANCE' as IPDDepositPurpose,
    receiptNumber: '',
    notes: '',
    paymentDate: toIstDatetimeLocalValue(new Date()),
    amount: '',
    splits: [{ mode: 'CASH' as PaymentMethod, amount: '' as string }],
    amountPendingFromThirdParty: false,
    pendingThirdPartyAmount: '',
  });
  const [statusReason, setStatusReason] = useState('');
  const [freezeRevenueOnSync, setFreezeRevenueOnSync] = useState(true);
  const [billDateLocal, setBillDateLocal] = useState('');
  const defaultChargeableRow = (): {
    type: ChargeableRowType;
    chargeCode: string;
    description: string;
    doctorId?: string;
    quantity: string;
    unitRate: string;
    serviceDate: string;
    labTestIds?: string[];
  } => ({
    type: 'PHARMA',
    chargeCode: '',
    description: '',
    doctorId: undefined,
    quantity: '1',
    unitRate: '',
    serviceDate: new Date().toISOString().slice(0, 10),
  });
  const [addItemsOpen, setAddItemsOpen] = useState(false);
  const [chargeableRows, setChargeableRows] = useState<ReturnType<typeof defaultChargeableRow>[]>([defaultChargeableRow()]);
  const [addItemsSubmitting, setAddItemsSubmitting] = useState(false);
  const [labTestSearchByRow, setLabTestSearchByRow] = useState<Record<number, string>>({});
  const [openDateRowIndex, setOpenDateRowIndex] = useState<number | null>(null);
  const [doctors, setDoctors] = useState<Array<{ id: string; name: string; specialisation?: string | null }>>([]);
  const [addDoctorOpen, setAddDoctorOpen] = useState(false);
  const [addDoctorRowIndex, setAddDoctorRowIndex] = useState<number | null>(null);
  const [newDoctorName, setNewDoctorName] = useState('');
  const [newDoctorSpec, setNewDoctorSpec] = useState<string>('');
  const [creatingDoctor, setCreatingDoctor] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [editingRefund, setEditingRefund] = useState<IPDRefund | null>(null);
  const [refundForm, setRefundForm] = useState({
    amount: '',
    paymentMode: 'CASH' as PaymentMethod,
    reason: '',
  });
  const [insuranceCaseOpen, setInsuranceCaseOpen] = useState(false);
  const [insuranceForm, setInsuranceForm] = useState({
    payerName: '',
    policyNumber: '',
    tpaName: '',
    coverageType: '',
    sumInsured: '',
  });
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteForm, setNoteForm] = useState({ noteType: 'CREDIT' as 'CREDIT' | 'DEBIT', amount: '', reason: '' });
  const [pharmaReceptionOpen, setPharmaReceptionOpen] = useState(false);
  const [pharmaReceptionRow, setPharmaReceptionRow] = useState<IPDBillItem | null>(null);
  const [pharmaReceptionMode, setPharmaReceptionMode] = useState<PaymentMethod>('CASH');

  const [settlePendingId, setSettlePendingId] = useState<string | null>(null);
  const [settleForm, setSettleForm] = useState({
    settledAmount: '',
    paymentMode: 'BANK_TRANSFER' as PaymentMethod,
    receiptNumber: '',
    paymentDate: toIstDatetimeLocalValue(new Date()),
    notes: '',
  });

  // Discount input drafts (avoid API call on every keypress)
  const [draftDiscountAmount, setDraftDiscountAmount] = useState<string>('');
  const [billEditRequestOpen, setBillEditRequestOpen] = useState(false);
  const [billEditRequestReason, setBillEditRequestReason] = useState('');

  // Custom chargeable types created from "Other" descriptions, persisted per browser
  const [customChargeableTypes, setCustomChargeableTypes] = useState<{ value: ChargeableRowType; label: string }[]>([]);

  // Admission context (patient name)
  const { data: admissionRes } = useQuery({
    queryKey: ['ipd-admission', admissionId],
    queryFn: () => ipdApi.getAdmissionById(admissionId!),
    enabled: !!admissionId,
  });
  const admission = admissionRes?.data?.data;
  const patientName = admission?.queue?.patient?.name ?? 'Patient';
  const offlineSyncedAt = admission?.offlineSettlementSyncedAt
    ? new Date(admission.offlineSettlementSyncedAt)
    : null;

  const { data: revenueEditLock, refetch: refetchEditLock } = useQuery({
    queryKey: ['ipd-revenue-bill-edit-lock', admissionId],
    enabled: !!admissionId && !isOfflineBilling && isReceptionistOnly,
    queryFn: async () => {
      const res = await ipdApi.getRevenueBillEditLock(admissionId!);
      return res.data?.data;
    },
  });

  const canEditRevenueBill =
    canMutateIpd &&
    (isOfflineBilling ||
      user?.role === 'HOSPITAL_ADMIN' ||
      user?.role === 'SUPER_ADMIN' ||
      !revenueEditLock?.locked);

  const isHospitalAdmin =
    user?.role === 'HOSPITAL_ADMIN' || user?.role === 'SUPER_ADMIN';
  const canDeletePendingThirdParty = isHospitalAdmin;

  const revenueBillLocked =
    !isOfflineBilling && isReceptionistOnly && !!revenueEditLock?.locked;

  const billEditRequestMut = useMutation({
    mutationFn: (reason: string) => ipdApi.createBillEditRequest(admissionId!, reason),
    onSuccess: () => {
      toast.success('Edit request sent to hospital admin');
      setBillEditRequestOpen(false);
      setBillEditRequestReason('');
      void refetchEditLock();
      queryClient.invalidateQueries({ queryKey: ['ipd-bill-edit-requests'] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message || 'Failed to submit request'),
  });

  // Bill & details (404 = no bill yet, treat as null)
  const { data: billRes, isLoading: billLoading } = useQuery({
    queryKey: ['ipd-bill-details', admissionId, billTrack],
    queryFn: async () => {
      try {
        const r = await ipdBillingApi.getBillDetails(admissionId!, billTrack);
        return r.data?.data ?? null;
      } catch (e: any) {
        if (e.response?.status === 404) return null;
        throw e;
      }
    },
    enabled: !!admissionId,
  });
  const billDetails = billRes ?? undefined;

  // Running balance
  const { data: balanceRes } = useQuery({
    queryKey: ['ipd-balance', admissionId, billTrack],
    queryFn: () => ipdBillingApi.getRunningBalance(admissionId!, billTrack),
    enabled: !!admissionId,
  });
  const balance = balanceRes?.data?.data;
  const canManageDeposits =
    isHospitalAdmin || !!balance?.hasThirdPartyCollectionCase;

  // Deposits
  const { data: depositsRes } = useQuery({
    queryKey: ['ipd-deposits', admissionId, billTrack],
    queryFn: () => ipdBillingApi.listDeposits(admissionId!, billTrack),
    enabled: !!admissionId,
  });
  const deposits = depositsRes?.data?.data ?? [];

  const { data: pendingCollectionsRes } = useQuery({
    queryKey: ['ipd-admission-pending-collections', admissionId, billTrack],
    queryFn: () => ipdBillingApi.listAdmissionPendingCollections(admissionId!, billTrack),
    enabled: !!admissionId,
  });
  const pendingCollections: IPDPendingCollection[] =
    pendingCollectionsRes?.data?.data ?? [];
  const openPendingCollections = pendingCollections.filter((p) => p.status === 'PENDING');
  const consolidatedPendingTotal = Math.round(
    openPendingCollections.reduce((s, p) => s + Number(p.amount || 0), 0) * 100
  ) / 100;

  const collectPendingMut = useMutation({
    mutationFn: (settledAmount: number) =>
      ipdBillingApi.collectAllPendingForAdmission(
        admissionId!,
        {
          paymentMode: settleForm.paymentMode,
          settledAmount,
          receiptNumber: settleForm.receiptNumber.trim() || undefined,
          paymentDate: settleForm.paymentDate
            ? istDatetimeLocalToIso(settleForm.paymentDate)
            : undefined,
          notes: settleForm.notes.trim() || undefined,
        },
        billTrack
      ),
    onSuccess: (res) => {
      const settled = res.data?.data?.settledAmount;
      toast.success(
        settled != null
          ? `Consolidated settlement recorded (₹${Number(settled).toLocaleString('en-IN')})`
          : 'Consolidated settlement recorded'
      );
      setSettlePendingId(null);
      void queryClient.invalidateQueries({ queryKey: ['ipd-balance', admissionId] });
      void queryClient.invalidateQueries({ queryKey: ['ipd-deposits', admissionId] });
      void queryClient.invalidateQueries({ queryKey: ['ipd-admission-pending-collections', admissionId] });
      void queryClient.invalidateQueries({ queryKey: ['ipd-pending-collections'] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message || 'Failed to record settled amount'),
  });

  // Refunds
  const { data: refundsRes } = useQuery({
    queryKey: ['ipd-refunds', admissionId, billTrack],
    queryFn: () => ipdBillingApi.listRefunds(admissionId!, billTrack),
    enabled: !!admissionId,
  });
  const refunds = refundsRes?.data?.data ?? [];

  // Settlement
  const { data: settleRes } = useQuery({
    queryKey: ['ipd-settle', admissionId, billTrack],
    queryFn: () => ipdBillingApi.settleAtDischarge(admissionId!, billTrack),
    enabled: !!admissionId && activeTab === 'refunds',
  });
  const settlement = settleRes?.data?.data;

  // Chargeable items
  const { data: chargeableRes } = useQuery({
    queryKey: ['ipd-chargeable', admissionId],
    queryFn: () => ipdBillingApi.listChargeableItems(admissionId!),
    enabled: !!admissionId,
  });
  const chargeableItems = chargeableRes?.data?.data ?? [];

  const isPackagePatientFlag = useMemo(() => {
    const adm: any = billDetails?.admission;
    return isPackagePatient(adm?.totalPackageCost);
  }, [billDetails?.admission]);

  const packageType = useMemo(() => {
    const adm: any = billDetails?.admission;
    return resolvePackageType(adm);
  }, [billDetails?.admission]);

  const showBillSeparatelyColumn =
    isPackagePatientFlag && packageType != null && isMedicineIncludedInPackage(packageType);

  // Insurance case (404 = no case yet)
  const { data: insuranceRes } = useQuery({
    queryKey: ['ipd-insurance-case', admissionId],
    queryFn: async () => {
      try {
        const r = await ipdBillingApi.getInsuranceCase(admissionId!, billTrack);
        return r.data?.data ?? null;
      } catch (e: any) {
        if (e.response?.status === 404) return null;
        throw e;
      }
    },
    enabled: !!admissionId,
  });
  const insuranceCase = insuranceRes ?? undefined;

  const isInsuranceOrCashlessCase = useMemo(() => {
    const adm: any = billDetails?.admission;
    const insuranceType = String(adm?.insuranceType ?? 'NA').toUpperCase();
    return (
      insuranceType === 'CASHLESS' ||
      insuranceType === 'REIMBURSEMENT' ||
      billDetails?.billType === 'INSURANCE' ||
      !!adm?.insuranceCase ||
      !!insuranceCase
    );
  }, [billDetails?.admission, billDetails?.billType, insuranceCase]);

  const packageHidesPharmacy =
    isPackagePatientFlag && packageType != null && isMedicineIncludedInPackage(packageType);
  const packageHidesLab =
    isPackagePatientFlag && packageType != null && isLabIncludedInPackage(packageType);

  const showPackageLineIncludeControls =
    !!billDetails &&
    billDetails.status === 'DRAFT' &&
    isInsuranceOrCashlessCase &&
    isPackagePatientFlag &&
    (packageHidesPharmacy || packageHidesLab);

  // Claim view (after adjudication)
  const [claimView, setClaimView] = useState<any>(null);

  // Notes & audit (when bill exists)
  const billId = billDetails?.id;
  const canEditChargeables =
    canMutateIpd && (!billDetails || billDetails.status === 'DRAFT');

  useEffect(() => {
    if (!billDetails) {
      setBillDateLocal('');
      return;
    }
    const src = billDetails.billDate ?? billDetails.frozenAt ?? new Date().toISOString();
    setBillDateLocal(toIstDatetimeLocalValue(src));
  }, [billDetails?.id, billDetails?.billDate, billDetails?.frozenAt]);

  const updateBillDateMut = useMutation({
    mutationFn: (iso: string) => ipdBillingApi.updateBillDate(billId!, iso),
    onSuccess: () => {
      toast.success('Bill date updated for final bill PDF');
      queryClient.invalidateQueries({ queryKey: ['ipd-bill-details', admissionId, billTrack] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message || 'Failed to update bill date'),
  });
  const { data: notesRes } = useQuery({
    queryKey: ['ipd-notes', billId],
    queryFn: () => ipdBillingApi.listCreditDebitNotes(billId!),
    enabled: !!billId && activeTab === 'notes',
  });
  const { data: auditRes } = useQuery({
    queryKey: ['ipd-audit', billId],
    queryFn: () => ipdBillingApi.listBillAuditLog(billId!),
    enabled: !!billId && activeTab === 'notes',
  });
  const notes = notesRes?.data?.data ?? [];
  const auditLogs = auditRes?.data?.data ?? [];

  // Load saved custom chargeable types from localStorage (per browser)
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem('ipd-chargeable-custom-types');
      if (raw) {
        const parsed = JSON.parse(raw) as { value: ChargeableRowType; label: string }[];
        if (Array.isArray(parsed)) {
          setCustomChargeableTypes(parsed);
        }
      }
    } catch {
      // ignore
    }
  }, []);

  // Lab tests master (for adding lab tests as chargeable items)
  const { data: labTestsMaster = [] } = useQuery<LabTest[]>({
    queryKey: ['lab-tests'],
    queryFn: async () => {
      const res = await labApi.getLabTests();
      const raw = res.data as { data?: LabTest[] } | LabTest[] | undefined;
      return Array.isArray(raw) ? raw : (raw?.data ?? []);
    },
  });

  const fetchDoctors = async () => {
    try {
      const r = await ipdApi.getDoctors();
      setDoctors(r.data?.data ?? []);
    } catch {
      // ignore
    }
  };

  const focusNextFieldWithin = (container: HTMLElement, current: HTMLElement) => {
    const focusables = Array.from(
      container.querySelectorAll<HTMLElement>(
        [
          'input:not([type="hidden"]):not([disabled])',
          'textarea:not([disabled])',
          'button:not([disabled])',
          '[tabindex]:not([tabindex="-1"])'
        ].join(',')
      )
    ).filter((el) => {
      if (el.getAttribute('aria-hidden') === 'true') return false;
      if (el.closest('[aria-hidden="true"]')) return false;
      // Skip "remove row" buttons and footer actions; keep this for data entry fields.
      if (el.getAttribute('type') === 'button' && el.textContent?.trim() === '×') return false;
      if (el.closest('footer')) return false;
      return true;
    });

    const idx = focusables.indexOf(current);
    const next = idx >= 0 ? focusables[idx + 1] : focusables[0];
    next?.focus();
  };

  const handleEnterToNextField = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;
    const target = e.target as HTMLElement | null;
    if (!target) return;
    // Allow multi-line inputs to keep Enter behavior.
    if (target instanceof HTMLTextAreaElement) return;
    // Only apply to input-like typing; avoid breaking buttons/select triggers.
    if (!(target instanceof HTMLInputElement)) return;
    if (target.type === 'checkbox' || target.type === 'radio' || target.type === 'button' || target.type === 'submit') {
      return;
    }

    const container = target.closest('[data-enter-next-scope="true"]') as HTMLElement | null;
    if (!container) return;
    e.preventDefault();
    focusNextFieldWithin(container, target);
  };

  useEffect(() => {
    if (addItemsOpen) fetchDoctors();
  }, [addItemsOpen]);

  const handleQuickCreateDoctor = async () => {
    const nm = newDoctorName.trim();
    if (!nm) {
      toast.error('Doctor name is required');
      return;
    }
    setCreatingDoctor(true);
    try {
      const resp = await ipdApi.quickCreateDoctor({
        name: nm,
        specialisation: newDoctorSpec || undefined,
      });
      const created = resp.data?.data;
      toast.success('Doctor added');
      setAddDoctorOpen(false);
      setNewDoctorName('');
      setNewDoctorSpec('');
      await fetchDoctors();
      if (created?.id != null && addDoctorRowIndex != null) {
        setChargeableRows((prev) =>
          prev.map((r, i) =>
            i === addDoctorRowIndex
              ? { ...r, doctorId: created.id, description: ensureDoctorPrefix(created.name) }
              : r
          )
        );
      }
      setAddDoctorRowIndex(null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to add doctor');
    } finally {
      setCreatingDoctor(false);
    }
  };

  const runAccrual = useMutation({
    mutationFn: () => ipdBillingApi.runAccrual(admissionId!, billTrack),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipd-bill-details', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-balance', admissionId] });
      toast.success('Accrual completed');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Accrual failed'),
  });

  const updateStatus = useMutation({
    mutationFn: async ({ status, reason }: { status: IPDBillStatus; reason?: string }) => {
      const res = await ipdBillingApi.updateBillStatus(billId!, { status, reason });
      return res.data;
    },
    onSuccess: (body) => {
      queryClient.invalidateQueries({ queryKey: ['ipd-bill-details', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-audit', billId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-admission', admissionId] });
      setStatusReason('');
      const sync = body?.data?.settlementSync;
      if (sync) {
        toast.success('Bill frozen; settlement auto-copied to revenue bill');
      } else {
        toast.success(body?.message || 'Status updated');
      }
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Update failed'),
  });

  const copySettlementMut = useMutation({
    mutationFn: () =>
      ipdApi.copyOfflineSettlementToRevenue(admissionId!, {
        freezeRevenueAfter: freezeRevenueOnSync,
      }),
    onSuccess: (res) => {
      const d = res.data?.data;
      queryClient.invalidateQueries({ queryKey: ['ipd-admission', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-bill-details', admissionId] });
      toast.success(
        d
          ? `Copied ${d.itemCount} line(s) to revenue bill (net ₹${Number(d.revenueNetAmount).toFixed(2)})`
          : 'Settlement copied to revenue bill'
      );
    },
    onError: (e: any) =>
      toast.error(e.response?.data?.message || 'Failed to copy settlement to revenue bill'),
  });

  const deleteOfflineBillMut = useMutation({
    mutationFn: () => ipdApi.deleteOfflineBilling(admissionId!),
    onSuccess: () => {
      toast.success('Offline bill deleted — patient removed from offline queue');
      navigate(`${basePath}/ipd-offline-queue`);
    },
    onError: (e: any) =>
      toast.error(e.response?.data?.message || 'Failed to delete offline bill'),
  });

  const updatePharmacyMode = useMutation({
    mutationFn: (mode: 'DETAILED' | 'SUMMARY' | 'HIDDEN_IN_PACKAGE') =>
      ipdBillingApi.updatePharmacyDisplayMode(billId!, { mode }),
    onSuccess: async () => {
      toast.success('Pharmacy visibility updated');
      await queryClient.invalidateQueries({ queryKey: ['ipd-bill-details', admissionId] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Failed to update pharmacy visibility'),
  });

  const updatePackageLineInclude = useMutation({
    mutationFn: (mode: 'PHARMACY' | 'LAB' | 'BOTH' | 'NONE') =>
      ipdBillingApi.updatePackageLineInclude(billId!, { mode }),
    onSuccess: async (_res, mode) => {
      const label =
        mode === 'PHARMACY'
          ? 'Pharmacy'
          : mode === 'LAB'
            ? 'Lab'
            : mode === 'BOTH'
              ? 'Pharmacy + Lab'
              : 'none';
      toast.success(
        mode === 'NONE'
          ? 'Package line include cleared; re-ran accrual'
          : `Included ${label} in bill lines; re-ran accrual`
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ipd-bill-details', admissionId] }),
        queryClient.invalidateQueries({ queryKey: ['ipd-balance', admissionId] }),
      ]);
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.message ?? 'Failed to update package line include'),
  });

  const updateDiscount = useMutation({
    mutationFn: (data: { discountPercentage?: number; discountAmount?: number }) =>
      ipdBillingApi.updateBillDiscount(billId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipd-bill-details', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-balance', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-pending-collections'] });
      toast.success('Discount updated');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to update discount'),
  });

  const updateRoomRateMut = useMutation({
    mutationFn: ({ billId, itemId, unitRate }: { billId: string; itemId: string; unitRate: number }) =>
      ipdBillingApi.updateBillItemRate(billId, itemId, unitRate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipd-bill-details', admissionId] });
      toast.success('Room rate updated');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to update room rate'),
  });

  const [editingRoomRate, setEditingRoomRate] = useState<Record<string, string>>({});

  // Bill line item edit / delete (DRAFT only)
  const [editingItem, setEditingItem] = useState<IPDBillItem | null>(null);
  const [editItemForm, setEditItemForm] = useState({ description: '', quantity: '', unitRate: '', serviceDate: '' });
  const [deleteConfirmItemId, setDeleteConfirmItemId] = useState<string | null>(null);
  const [otConsumptionExpanded, setOtConsumptionExpanded] = useState(false);
  const [editingChargeable, setEditingChargeable] = useState<IPDChargeableItem | null>(null);
  const [chargeableEditForm, setChargeableEditForm] = useState({
    description: '',
    quantity: '',
    unitRate: '',
    serviceDate: '',
  });
  const [deleteChargeableId, setDeleteChargeableId] = useState<string | null>(null);

  const updateBillItemMut = useMutation({
    mutationFn: ({
      billId,
      itemId,
      data,
    }: {
      billId: string;
      itemId: string;
      data: { description?: string; quantity?: number; unitRate?: number; serviceDate?: string };
    }) => ipdBillingApi.updateBillItem(billId, itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipd-bill-details', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-balance', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-pending-collections'] });
      setEditingItem(null);
      toast.success('Line item updated');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to update line item'),
  });

  const deleteBillItemMut = useMutation({
    mutationFn: ({ billId, itemId }: { billId: string; itemId: string }) =>
      ipdBillingApi.deleteBillItem(billId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipd-bill-details', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-balance', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-pending-collections'] });
      setDeleteConfirmItemId(null);
      toast.success('Line item deleted');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to delete line item'),
  });

  const updateChargeableMut = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { description?: string; quantity?: number; unitRate?: number; serviceDate?: string };
    }) => ipdBillingApi.updateChargeableItem(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipd-chargeable', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-bill-details', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-balance', admissionId] });
      setEditingChargeable(null);
      toast.success('Chargeable item updated. Run accrual again to refresh bill lines.');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to update chargeable item'),
  });

  const toggleChargeableBillSeparately = useMutation({
    mutationFn: async (args: { id: string; billSeparately: boolean }) => {
      return ipdBillingApi.updateChargeableItem(args.id, { billSeparately: args.billSeparately });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipd-chargeable', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-bill-details', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-balance', admissionId] });
      toast.success('Updated package billing preference. Run accrual to refresh bill lines.');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to update billing preference'),
  });

  const deleteChargeableMut = useMutation({
    mutationFn: (id: string) => ipdBillingApi.deleteChargeableItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipd-chargeable', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-bill-details', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-balance', admissionId] });
      setDeleteChargeableId(null);
      toast.success('Chargeable item removed. Run accrual to update the bill.');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to delete chargeable item'),
  });

  const addDepositMut = useMutation({
    mutationFn: (data: {
      amount?: number;
      splits?: { mode: PaymentMethod; amount: number }[];
      purpose: IPDDepositPurpose;
      receiptNumber?: string;
      paymentDate?: string;
      notes?: string;
      amountPendingFromThirdParty?: boolean;
      pendingThirdPartyAmount?: number;
    }) =>
      ipdBillingApi.addDeposit(admissionId!, data as any, billTrack),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['ipd-deposits', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-balance', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-settle', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-pending-collections'] });
      // Bill details reload is heavy on old admissions; refresh in background after deposit saves.
      void queryClient.invalidateQueries({ queryKey: ['ipd-bill-details', admissionId] });
      try {
        const payload = (res as any)?.data?.data;
        const created = payload?.deposits ?? payload;
        const first = Array.isArray(created) ? created[0] : created;
        const receiptNumber = first?.receiptNumber;
        const purpose = first?.purpose;
        const cashTotal = Array.isArray(created)
          ? created.reduce((s: number, d: { amount?: number; amountPendingFromThirdParty?: boolean }) =>
              s + (d?.amountPendingFromThirdParty ? 0 : (d?.amount ?? 0)), 0)
          : first?.amountPendingFromThirdParty
            ? 0
            : (first?.amount ?? 0);
        if (receiptNumber && (purpose === 'FULL_AND_FINAL' || first?.amountPendingFromThirdParty) && (cashTotal > 0 || first?.amountPendingFromThirdParty)) {
          window.open(`/api/ipd/billing/admission/${admissionId}/deposits/receipt/${encodeURIComponent(receiptNumber)}/pdf?billTrack=${billTrack || 'REVENUE'}`, '_blank');
        }
      } catch {
        // ignore
      }
      setDepositOpen(false);
      setDepositForm({
        amount: '',
        purpose: 'ADVANCE',
        receiptNumber: '',
        notes: '',
        paymentDate: toIstDatetimeLocalValue(new Date()),
        splits: [{ mode: 'CASH' as PaymentMethod, amount: '' }],
        amountPendingFromThirdParty: false,
        pendingThirdPartyAmount: '',
      });
      toast.success('Deposit recorded');
    },
    onError: async (e: any, variables) => {
      const status = e.response?.status;
      const timedOut =
        e.code === 'ECONNABORTED' || status === 502 || status === 504;
      if (timedOut && admissionId) {
        const expectedAmount =
          variables?.splits?.reduce((s: number, x: { amount: number }) => s + x.amount, 0) ??
          variables?.amount ??
          0;
        const prevOutstanding = balance?.outstanding ?? null;
        let saved = false;
        for (let attempt = 0; attempt < 5; attempt++) {
          await new Promise((r) => setTimeout(r, 1500));
          try {
            const balRes = await ipdBillingApi.getRunningBalance(admissionId, billTrack);
            const nextOutstanding = balRes?.data?.data?.outstanding;
            if (
              prevOutstanding != null &&
              typeof nextOutstanding === 'number' &&
              nextOutstanding < prevOutstanding - 0.009 &&
              Math.abs(prevOutstanding - nextOutstanding - expectedAmount) < 1
            ) {
              saved = true;
              break;
            }
          } catch {
            // keep polling
          }
        }
        queryClient.invalidateQueries({ queryKey: ['ipd-deposits', admissionId] });
        queryClient.invalidateQueries({ queryKey: ['ipd-balance', admissionId] });
        queryClient.invalidateQueries({ queryKey: ['ipd-settle', admissionId] });
        setDepositOpen(false);
        if (saved) {
          toast.success('Deposit saved successfully');
          return;
        }
        toast.error(
          'Request timed out. Check Deposits & Balance — if the amount already posted, do not submit again.'
        );
        return;
      }
      toast.error(e.response?.data?.message || 'Failed to add deposit');
    },
  });

  const addRefundMut = useMutation({
    mutationFn: (data: { amount: number; paymentMode: PaymentMethod; reason?: string }) =>
      ipdBillingApi.addRefund(admissionId!, data, billTrack),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipd-refunds', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-settle', admissionId] });
      setRefundOpen(false);
      setEditingRefund(null);
      setRefundForm({ amount: '', paymentMode: 'CASH', reason: '' });
      toast.success('Refund recorded');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to add refund'),
  });

  const updateRefundMut = useMutation({
    mutationFn: (payload: { refundId: string; amount: number; paymentMode: PaymentMethod; reason?: string }) =>
      ipdBillingApi.updateRefund(admissionId!, payload.refundId, {
        amount: payload.amount,
        paymentMode: payload.paymentMode,
        reason: payload.reason,
      }, billTrack),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipd-refunds', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-settle', admissionId] });
      setRefundOpen(false);
      setEditingRefund(null);
      setRefundForm({ amount: '', paymentMode: 'CASH', reason: '' });
      toast.success('Refund updated');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to update refund'),
  });

  const deleteRefundMut = useMutation({
    mutationFn: (refundId: string) => ipdBillingApi.deleteRefund(admissionId!, refundId, billTrack),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipd-refunds', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-settle', admissionId] });
      toast.success('Refund deleted');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to delete refund'),
  });

  const deleteDepositMut = useMutation({
    mutationFn: (depositId: string) => ipdBillingApi.deleteDeposit(admissionId!, depositId, billTrack),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ipd-deposits', admissionId] }),
        queryClient.invalidateQueries({ queryKey: ['ipd-balance', admissionId] }),
        queryClient.invalidateQueries({ queryKey: ['ipd-settle', admissionId] }),
        queryClient.invalidateQueries({ queryKey: ['ipd-pending-collections'] }),
        queryClient.invalidateQueries({
          queryKey: ['ipd-admission-pending-collections', admissionId],
        }),
      ]);
      // Force outstanding / deposits strip to refresh immediately after receipt removal.
      await queryClient.refetchQueries({ queryKey: ['ipd-balance', admissionId] });
      toast.success('Deposit removed — outstanding updated');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to delete deposit'),
  });

  const deletePendingThirdPartyMut = useMutation({
    mutationFn: (pendingId: string) => ipdBillingApi.deletePendingCollection(pendingId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ipd-deposits', admissionId] }),
        queryClient.invalidateQueries({ queryKey: ['ipd-balance', admissionId] }),
        queryClient.invalidateQueries({ queryKey: ['ipd-settle', admissionId] }),
        queryClient.invalidateQueries({ queryKey: ['ipd-pending-collections'] }),
        queryClient.invalidateQueries({
          queryKey: ['ipd-admission-pending-collections', admissionId],
        }),
      ]);
      await queryClient.refetchQueries({ queryKey: ['ipd-balance', admissionId] });
      toast.success('Pending third-party amount deleted');
    },
    onError: (e: any) =>
      toast.error(e.response?.data?.message || 'Failed to delete pending third-party amount'),
  });

  const updateDepositMut = useMutation({
    mutationFn: (payload: {
      depositId: string;
      amount: number;
      mode: PaymentMethod;
      purpose: IPDDepositPurpose;
      paymentDate: string;
      receiptNumber?: string | null;
      notes?: string | null;
    }) =>
      ipdBillingApi.updateDeposit(
        admissionId!,
        payload.depositId,
        {
          amount: payload.amount,
          mode: payload.mode,
          purpose: payload.purpose,
          paymentDate: istDatetimeLocalToIso(payload.paymentDate),
          receiptNumber: payload.receiptNumber,
          notes: payload.notes,
        },
        billTrack
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ipd-deposits', admissionId] }),
        queryClient.invalidateQueries({ queryKey: ['ipd-balance', admissionId] }),
        queryClient.invalidateQueries({ queryKey: ['ipd-settle', admissionId] }),
        queryClient.invalidateQueries({ queryKey: ['ipd-pending-collections'] }),
      ]);
      await queryClient.refetchQueries({ queryKey: ['ipd-balance', admissionId] });
      setEditingDeposit(null);
      toast.success('Deposit updated — outstanding refreshed');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to update deposit'),
  });

  const settlePharmaReceptionMut = useMutation({
    mutationFn: () => {
      if (!admissionId || !pharmaReceptionRow?.sourceId) throw new Error('Missing admission or chargeable');
      return ipdBillingApi.settlePharmaAtReception(admissionId, {
        chargeableIds: [pharmaReceptionRow.sourceId],
        paymentMethod: pharmaReceptionMode,
      }, billTrack);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipd-bill-details', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-balance', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-deposits', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-chargeable', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['ipd-settle', admissionId] });
      setPharmaReceptionOpen(false);
      setPharmaReceptionRow(null);
      toast.success('Marked as collected at reception');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to settle pharma line'),
  });

  useMutation({
    mutationFn: (data: { sourceType: IPDChargeSourceType; chargeCode: string; description: string; quantity: number; unitRate: number; serviceDate?: string }) =>
      ipdBillingApi.addChargeableItem(admissionId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipd-chargeable', admissionId] });
      toast.success('Item added. Run accrual to include in bill.');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to add item'),
  });

  const createOrGetInsuranceMut = useMutation({
    mutationFn: (data: { payerName: string; policyNumber: string; tpaName?: string; coverageType?: string; sumInsured?: number }) =>
      ipdBillingApi.getOrCreateInsuranceCase(admissionId!, data, billTrack),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipd-insurance-case', admissionId] });
      setInsuranceCaseOpen(false);
      toast.success('Insurance case saved');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to save insurance case'),
  });

  const runAdjudicationMut = useMutation({
    mutationFn: () => ipdBillingApi.runAdjudication(admissionId!, billTrack),
    onSuccess: (res) => {
      setClaimView(res.data?.data ?? null);
      toast.success('Adjudication completed');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Adjudication failed'),
  });

  const createNoteMut = useMutation({
    mutationFn: (data: { noteType: 'CREDIT' | 'DEBIT'; amount: number; reason: string }) =>
      ipdBillingApi.createCreditDebitNote(billId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipd-notes', billId] });
      setNoteOpen(false);
      setNoteForm({ noteType: 'CREDIT', amount: '', reason: '' });
      toast.success('Note created');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to create note'),
  });

  const isOnFrontDeskRoute =
    location.pathname.startsWith('/receptionist/') ||
    location.pathname.startsWith('/hospital-admin/');
  const backUrl = isOnFrontDeskRoute
    ? isOfflineBilling
      ? `${basePath}/ipd-offline-queue`
      : `${basePath}/ipd-queue`
    : `/doctor/ipd/${admissionId}`;
  const billEditBlocked = !canEditRevenueBill;

  const handleDownloadFinalBillPDF = async () => {
    if (!admissionId) return;
    try {
      const res = await ipdBillingApi.getFinalBillPDF(admissionId, billTrack);
      const blob = res.data as unknown as Blob;
      const contentType = res.headers?.['content-type'] ?? '';
      if (res.status !== 200 || (!contentType.includes('application/pdf') && blob?.type !== 'application/pdf')) {
        const text = typeof blob === 'object' && blob instanceof Blob ? await blob.text() : String(blob);
        let msg = 'Failed to download PDF';
        try {
          const j = JSON.parse(text);
          if (j?.message) msg = j.message;
        } catch { /* ignore */ }
        toast.error(msg);
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `IPD-Final-Bill-${admissionId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Final bill PDF downloaded');
    } catch (e: any) {
      let message = 'Failed to download PDF';
      const data = e.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const j = JSON.parse(text);
          if (j?.message) message = j.message;
        } catch { /* ignore */ }
      } else if (data?.message) {
        message = data.message;
      }
      toast.error(message);
    }
  };

  if (!isFrontDesk) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
          <p className="font-medium text-amber-800">Access restricted</p>
          <p className="text-sm text-amber-700 mt-1">
            IPD billing can only be performed by reception or hospital admin. Clinical inputs are
            integrated into the bill when front desk runs accrual.
          </p>
          <Button className="mt-4" variant="outline" onClick={() => navigate('/doctor/ipd-management')}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  if (!admissionId) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate(backUrl)}><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
        <p className="text-destructive mt-4">Admission ID missing.</p>
      </div>
    );
  }

  const bill = billDetails;
  const pharmaPaidAtPharmacyTotal = useMemo(() => {
    if (!bill?.billItems?.length) return 0;
    return bill.billItems
      .filter((i) => i.sourceType === 'PHARMA' && i.pharmaBillingStatus === 'PAID_AT_PHARMACY')
      .reduce((s, i) => s + (Number(i.netAmount) || 0), 0);
  }, [bill?.id, bill?.billItems]);
  useEffect(() => {
    if (!bill) return;
    setDraftDiscountAmount(String((bill as any).discountAmount ?? 0));
  }, [bill?.id, (bill as any)?.discountAmount]);
  const statusColor: Record<string, string> = {
    DRAFT: 'bg-amber-100 text-amber-800',
    REVIEWED: 'bg-blue-100 text-blue-800',
    APPROVED: 'bg-emerald-100 text-emerald-800',
    FROZEN: 'bg-slate-200 text-slate-800',
  };

  return (
    <div className="thms-page-shell">
      {!canMutateIpd && user?.role === 'OPERATIONS_MANAGER' ? (
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          View only — Operations Manager can review IPD billing but cannot change bills, deposits, or collections.
        </div>
      ) : null}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(backUrl)} title="Back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold">
              {isOfflineBilling ? 'IPD Offline Billing' : 'IPD Billing'}
            </h1>
            <p className="text-muted-foreground">{patientName} · Admission</p>
            {admission?.financingPartnerName ? (
              <p className="text-sm text-teal-700 mt-0.5">
                Financing partner: <strong>{admission.financingPartnerName}</strong>
              </p>
            ) : null}
            {isOfflineBilling && (
              <p className="text-sm text-amber-700 mt-1">
                Settlement / insurance workflow — amounts here are not included in revenue or business reports.
                Discharge requires only this offline bill to be settled (main revenue bill balance is not required).
              </p>
            )}
            {isOfflineBilling && offlineSyncedAt && (
              <p className="text-xs text-muted-foreground mt-1">
                Last copied to revenue bill: {format(offlineSyncedAt, 'dd/MM/yyyy, h:mm a')}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 sm:flex-row">
          {isOfflineBilling && (
            <div className="flex flex-col items-end gap-2 sm:flex-row sm:items-center">
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <Checkbox
                  checked={freezeRevenueOnSync}
                  onCheckedChange={(v) => setFreezeRevenueOnSync(v === true)}
                />
                Freeze revenue bill after copy
              </label>
              <Button
                variant="secondary"
                disabled={copySettlementMut.isPending || !bill}
                onClick={() => copySettlementMut.mutate()}
                className="flex items-center gap-2"
              >
                <Copy className="h-4 w-4" />
                {copySettlementMut.isPending ? 'Copying…' : 'Copy settlement to revenue bill'}
              </Button>
              {canManageDeposits && (
                <Button
                  variant="outline"
                  className="text-red-700 border-red-200 hover:bg-red-50"
                  disabled={deleteOfflineBillMut.isPending}
                  onClick={() => {
                    const confirmed = window.confirm(
                      'Delete this offline (OIPD) bill?\n\n' +
                        'The patient will be removed from the offline IPD queue.\n' +
                        'Medicines, services, and other components are NOT deleted.\n' +
                        'The main revenue IPD bill is unchanged.'
                    );
                    if (confirmed) deleteOfflineBillMut.mutate();
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  {deleteOfflineBillMut.isPending ? 'Deleting…' : 'Delete offline bill'}
                </Button>
              )}
            </div>
          )}
          {bill && (
            <div className="flex flex-col items-end gap-1">
              <Label htmlFor="ipd-bill-date" className="text-xs text-muted-foreground">
                Bill date & time on PDF (IST)
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="ipd-bill-date"
                  type="datetime-local"
                  className="w-[220px] h-9"
                  value={billDateLocal}
                  onChange={(e) => setBillDateLocal(e.target.value)}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={!billDateLocal || updateBillDateMut.isPending}
                  onClick={() => {
                    const iso = istDatetimeLocalToIso(billDateLocal);
                    if (!iso) {
                      toast.error('Enter a valid date and time');
                      return;
                    }
                    updateBillDateMut.mutate(iso);
                  }}
                >
                  {updateBillDateMut.isPending ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          )}
          <Button variant="outline" onClick={handleDownloadFinalBillPDF} className="flex items-center gap-2">
            <FileDown className="h-4 w-4" /> Download Final Bill PDF
          </Button>
        </div>
      </div>

      {admission?.status === 'ADMITTED' && admissionId && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Package & surgery</CardTitle>
            <p className="text-sm text-muted-foreground font-normal">
              {isPackagePatientFlag && packageType
                ? `Current: ${packageTypeLabel(packageType)}`
                : 'Set a package amount and type for this admission, or leave empty for itemised billing.'}
            </p>
          </CardHeader>
          <CardContent>
            <AdmissionPackageEditor
              admissionId={admissionId}
              initial={{
                surgeryName: admission.surgeryName,
                totalPackageCost: admission.totalPackageCost,
                packageType: admission.packageType,
                gstApplicable: admission.gstApplicable,
              }}
              onSaved={() => {
                void queryClient.invalidateQueries({ queryKey: ['ipd-admission', admissionId] });
                void queryClient.invalidateQueries({ queryKey: ['ipd-bill-details', admissionId] });
              }}
            />
          </CardContent>
        </Card>
      )}

      {revenueBillLocked && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex gap-3">
            <Lock className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-900">Revenue bill locked after discharge</p>
              <p className="text-sm text-amber-800 mt-1">
                {revenueEditLock?.reason ??
                  'Changes require hospital admin approval. Submit a request with a reason.'}
              </p>
              {revenueEditLock?.pendingRequest && (
                <p className="text-xs text-amber-700 mt-2">
                  Pending request submitted{' '}
                  {format(new Date(revenueEditLock.pendingRequest.createdAt), 'dd/MM/yyyy, h:mm a')}
                </p>
              )}
            </div>
          </div>
          {!revenueEditLock?.pendingRequest && (
            <Button variant="outline" onClick={() => setBillEditRequestOpen(true)}>
              Request admin approval
            </Button>
          )}
        </div>
      )}

      {revenueEditLock?.unlockedUntil && isReceptionistOnly && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          Admin approved bill edits until{' '}
          {format(new Date(revenueEditLock.unlockedUntil), 'dd/MM/yyyy, h:mm a')}.
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-6 gap-1">
          <TabsTrigger value="bill" className="flex items-center gap-1"><Receipt className="h-4 w-4" /> Bill & Items</TabsTrigger>
          <TabsTrigger value="deposits" className="flex items-center gap-1"><Wallet className="h-4 w-4" /> Deposits & Balance</TabsTrigger>
          <TabsTrigger value="refunds" className="flex items-center gap-1"><RefreshCw className="h-4 w-4" /> Refunds & Settlement</TabsTrigger>
          <TabsTrigger value="chargeable" className="flex items-center gap-1"><Pill className="h-4 w-4" /> Chargeable Items</TabsTrigger>
          <TabsTrigger value="insurance" className="flex items-center gap-1"><Shield className="h-4 w-4" /> Insurance & Claim</TabsTrigger>
          <TabsTrigger value="notes" className="flex items-center gap-1"><FileText className="h-4 w-4" /> Notes & Audit</TabsTrigger>
        </TabsList>

        {/* ——— Bill & Items ——— */}
        <TabsContent value="bill" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Bill & line items</CardTitle>
              <div className="flex flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-2">
                {(!bill || bill.status === 'DRAFT') ? (
                  <Button
                    onClick={() => runAccrual.mutate()}
                    disabled={runAccrual.isPending || billEditBlocked}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${runAccrual.isPending ? 'animate-spin' : ''}`} />
                    {bill ? 'Run accrual' : 'Create bill & run accrual'}
                  </Button>
                ) : bill ? (
                  <p className="text-sm text-muted-foreground">
                    Accrual can only be run when the bill is in DRAFT. This bill is {bill.status}.
                  </p>
                ) : null}
              </div>
            </CardHeader>
            <CardContent>
              {billLoading ? (
                <p className="text-muted-foreground">Loading bill...</p>
              ) : !bill ? (
                <p className="text-muted-foreground">No bill yet. Run accrual or open bill to create one.</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-4 mb-4">
                    <span className="font-medium">Bill # {bill.billNumber}</span>
                    <Badge className={statusColor[bill.status] ?? 'bg-gray-100'}>{bill.status}</Badge>
                    <span>Gross: {formatCurrency(bill.grossAmount)}</span>
                    <span>GST: {formatCurrency(bill.gstAmount)}</span>
                    {(bill.totalDiscount > 0 || (bill.discountAmount ?? 0) > 0) && (
                      <span className="text-orange-600">
                        {bill.discountAmount > 0
                          ? `Discount: ${formatCurrency(bill.discountAmount)}`
                          : `Discount (${bill.totalDiscount}%): ${formatCurrency(bill.grossAmount * bill.totalDiscount / 100)}`}
                      </span>
                    )}
                    <span className="font-semibold">Net: {formatCurrency(bill.netAmount)}</span>
                    {pharmaPaidAtPharmacyTotal > 0 && (
                      <span className="text-sm text-slate-600">
                        Medicines paid at pharmacy: {formatCurrency(pharmaPaidAtPharmacyTotal)}
                      </span>
                    )}
                  </div>
                  {bill.status === 'DRAFT' && (
                    <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-muted/50 rounded-lg">
                      <Label htmlFor="discount-pct-input" className="whitespace-nowrap">Discount (%):</Label>
                      <Input
                        id="discount-pct-input"
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        placeholder="0.00"
                        value={bill.totalDiscount ?? 0}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value) || 0;
                          if (val >= 0 && val <= 100) {
                            updateDiscount.mutate({ discountPercentage: val });
                          }
                        }}
                        className="w-24"
                        disabled={updateDiscount.isPending}
                      />
                      <span className="text-sm text-muted-foreground min-w-[10rem]">
                        {bill.totalDiscount > 0 ? `= ${formatCurrency(bill.grossAmount * bill.totalDiscount / 100)}` : ''}
                      </span>

                      <div className="h-6 w-px bg-border" />

                      <Label htmlFor="discount-amt-input" className="whitespace-nowrap">Discount amount (₹):</Label>
                      <Input
                        id="discount-amt-input"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={draftDiscountAmount}
                        onChange={(e) => {
                          setDraftDiscountAmount(e.target.value);
                        }}
                        onBlur={() => {
                          const val = parseFloat(draftDiscountAmount);
                          const next = isNaN(val) ? 0 : val;
                          const current = Number((bill as any).discountAmount ?? 0) || 0;
                          if (Math.abs(next - current) > 0.009) {
                            updateDiscount.mutate({ discountAmount: next });
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter') return;
                          const val = parseFloat(draftDiscountAmount);
                          const next = isNaN(val) ? 0 : val;
                          const current = Number((bill as any).discountAmount ?? 0) || 0;
                          if (Math.abs(next - current) > 0.009) {
                            updateDiscount.mutate({ discountAmount: next });
                          }
                        }}
                        className="w-32"
                        disabled={updateDiscount.isPending}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={
                          updateDiscount.isPending ||
                          Math.abs((Number.parseFloat(draftDiscountAmount) || 0) - (Number((bill as any).discountAmount ?? 0) || 0)) <= 0.009
                        }
                        onClick={() => {
                          const val = parseFloat(draftDiscountAmount);
                          updateDiscount.mutate({ discountAmount: isNaN(val) ? 0 : val });
                        }}
                      >
                        Apply
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        {bill.discountAmount > 0 && bill.grossAmount > 0
                          ? `≈ ${Math.round((bill.discountAmount / bill.grossAmount) * 10000) / 100}%`
                          : ''}
                      </span>
                    </div>
                  )}

                  {/* Receptionist-controlled pharmacy visibility (presentation layer) */}
                  <div className="flex flex-wrap items-center gap-3 mb-4 p-3 border rounded-lg bg-muted/20">
                    <div className="text-sm font-medium">Billing preferences</div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Pharmacy display:</span>
                      <Select
                        value={(bill.pharmacyDisplayMode as any) ?? 'DETAILED'}
                        onValueChange={(v) => updatePharmacyMode.mutate(v as any)}
                        disabled={bill.status === 'FROZEN' || !!insuranceCase}
                      >
                        <SelectTrigger className="w-64">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PHARMACY_DISPLAY_MODES.map((m) => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {insuranceCase && (
                      <div className="text-xs text-muted-foreground">
                        Insurance case: forced to Detailed for claim safety.
                      </div>
                    )}
                    {bill.status === 'FROZEN' && (
                      <div className="text-xs text-muted-foreground">
                        Locked after bill is frozen.
                      </div>
                    )}
                  </div>

                  {showPackageLineIncludeControls && (
                    <div className="mb-4 space-y-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-900 dark:bg-amber-950/30">
                      <div className="text-sm font-medium">
                        Insurance / cashless package — include line items
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Package normally hides pharmacy/lab inside the package amount. For insurance or
                        cashless cases you can add those costs as separate bill lines (and into the
                        overall total). Clicking re-runs accrual.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {packageHidesPharmacy && (
                          <Button
                            type="button"
                            size="sm"
                            variant={bill.includePackagePharmacy ? 'default' : 'outline'}
                            disabled={updatePackageLineInclude.isPending}
                            onClick={() => updatePackageLineInclude.mutate('PHARMACY')}
                          >
                            Include Pharmacy
                          </Button>
                        )}
                        {packageHidesLab && (
                          <Button
                            type="button"
                            size="sm"
                            variant={bill.includePackageLab && !bill.includePackagePharmacy ? 'default' : 'outline'}
                            disabled={updatePackageLineInclude.isPending}
                            onClick={() => updatePackageLineInclude.mutate('LAB')}
                          >
                            Include Lab
                          </Button>
                        )}
                        {packageHidesPharmacy && packageHidesLab && (
                          <Button
                            type="button"
                            size="sm"
                            variant={
                              bill.includePackagePharmacy && bill.includePackageLab ? 'default' : 'outline'
                            }
                            disabled={updatePackageLineInclude.isPending}
                            onClick={() => updatePackageLineInclude.mutate('BOTH')}
                          >
                            Include both
                          </Button>
                        )}
                        {(bill.includePackagePharmacy || bill.includePackageLab) && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            disabled={updatePackageLineInclude.isPending}
                            onClick={() => updatePackageLineInclude.mutate('NONE')}
                          >
                            Clear include
                          </Button>
                        )}
                      </div>
                      {(bill.includePackagePharmacy || bill.includePackageLab) && (
                        <p className="text-xs text-emerald-800 dark:text-emerald-300">
                          Active:{" "}
                          {[
                            bill.includePackagePharmacy ? 'Pharmacy' : null,
                            bill.includePackageLab ? 'Lab' : null,
                          ]
                            .filter(Boolean)
                            .join(' + ')}{' '}
                          included as line items.
                        </p>
                      )}
                    </div>
                  )}
                  {(bill.status as IPDBillStatus) !== 'FROZEN' || isHospitalAdmin ? (
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="text-sm text-muted-foreground self-center">
                        {(bill.status as IPDBillStatus) === 'FROZEN'
                          ? 'Admin: move frozen bill to'
                          : 'Change status:'}
                      </span>
                      {BILL_STATUSES.filter((s) => s !== bill.status).map((status) => (
                        <Button
                          key={status}
                          variant="outline"
                          size="sm"
                          disabled={updateStatus.isPending}
                          onClick={() => {
                            const leavingFrozen = (bill.status as IPDBillStatus) === 'FROZEN';
                            const freezing = status === 'FROZEN';
                            if (leavingFrozen && statusReason.trim().length < 5) {
                              toast.error('Enter a reason (min 5 characters) to unfreeze');
                              return;
                            }
                            const reason =
                              freezing || leavingFrozen ? statusReason.trim() || undefined : undefined;
                            updateStatus.mutate({ status, reason });
                          }}
                        >
                          Set {status}
                        </Button>
                      ))}
                      <Input
                        placeholder={
                          (bill.status as IPDBillStatus) === 'FROZEN'
                            ? 'Reason required to unfreeze'
                            : 'Reason (optional, required for FROZEN)'
                        }
                        value={statusReason}
                        onChange={(e) => setStatusReason(e.target.value)}
                        className="max-w-xs"
                      />
                    </div>
                  ) : null}
                  {bill.billItems?.length ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Code</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Source</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Rate</TableHead>
                          <TableHead className="text-right">Net</TableHead>
                          <TableHead className="min-w-[148px]">Pharma billing</TableHead>
                          {bill.status === 'DRAFT' && <TableHead className="w-[100px]">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(() => {
                          const rows = bill.billItems ?? [];
                          const isOtConsumption = (r: IPDBillItem) =>
                            r.sourceType === 'PHARMA' && r.chargeCode === 'OT_PHARMA';

                          const otRows = rows.filter(isOtConsumption);
                          const otherRows = rows.filter((r) => !isOtConsumption(r));

                          type BillingAgg = 'PENDING' | 'PAID_AT_PHARMACY' | 'PAID_AT_RECEPTION' | 'MIXED' | null;
                          const aggBilling = (items: IPDBillItem[]): BillingAgg => {
                            const set = new Set(
                              items.map((r) => (r.pharmaBillingStatus ? r.pharmaBillingStatus : 'PENDING'))
                            );
                            if (set.size === 0) return null;
                            if (set.size === 1) return Array.from(set)[0] as BillingAgg;
                            return 'MIXED';
                          };

                          const renderPharmaBilling = (row: IPDBillItem, override?: BillingAgg) => {
                            if (row.sourceType !== 'PHARMA') {
                              return <span className="text-muted-foreground">—</span>;
                            }
                            const st = override ?? (row.pharmaBillingStatus ? row.pharmaBillingStatus : 'PENDING');
                            return (
                              <div className="flex flex-col gap-1 items-start max-w-[200px]">
                                {st === 'PENDING' ? (
                                  <Badge variant="outline" className="text-amber-900 border-amber-700 bg-amber-50">
                                    Pending collection
                                  </Badge>
                                ) : st === 'PAID_AT_PHARMACY' ? (
                                  <Badge variant="outline" className="text-green-900 border-green-700 bg-green-50">
                                    Paid at pharmacy
                                  </Badge>
                                ) : st === 'PAID_AT_RECEPTION' ? (
                                  <Badge variant="outline" className="text-blue-900 border-blue-700 bg-blue-50">
                                    Collected at reception
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-slate-900 border-slate-300 bg-slate-50">
                                    Mixed
                                  </Badge>
                                )}
                                {canMutateIpd && st === 'PENDING' && row.sourceId && (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() => {
                                      setPharmaReceptionRow(row);
                                      setPharmaReceptionMode('CASH');
                                      setPharmaReceptionOpen(true);
                                    }}
                                  >
                                    Collect at desk
                                  </Button>
                                )}
                              </div>
                            );
                          };

                          const renderRow = (row: IPDBillItem, opts?: { indent?: boolean }) => {
                            const showDesc =
                              row.chargeCode === 'OT_PHARMA' && (row.drugName || row.drugStrength)
                                ? `${row.drugName ?? row.description}${row.drugStrength ? ` (${row.drugStrength})` : ''}`.trim()
                                : row.description;

                            return (
                              <TableRow key={row.id}>
                                <TableCell>{formatDate(row.serviceDate)}</TableCell>
                                <TableCell>{row.chargeCode}</TableCell>
                                <TableCell>{opts?.indent ? <span className="pl-6">{showDesc}</span> : showDesc}</TableCell>
                                <TableCell>{row.sourceType}</TableCell>
                                <TableCell className="text-right">{row.quantity}</TableCell>
                                <TableCell className="text-right">
                                  {bill.status === 'DRAFT' && (row.sourceType === 'ROOM' || row.sourceType === 'MISC') ? (
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min={0}
                                      className="h-8 w-24 text-right"
                                      value={editingRoomRate[row.id] ?? String(row.unitRate)}
                                      onChange={(e) => setEditingRoomRate((prev) => ({ ...prev, [row.id]: e.target.value }))}
                                      onBlur={() => {
                                        const raw = editingRoomRate[row.id] ?? String(row.unitRate);
                                        const num = parseFloat(raw);
                                        if (!isNaN(num) && num >= 0 && num !== row.unitRate && billId) {
                                          updateRoomRateMut.mutate({ billId, itemId: row.id, unitRate: num });
                                        }
                                        setEditingRoomRate((prev) => {
                                          const next = { ...prev };
                                          delete next[row.id];
                                          return next;
                                        });
                                      }}
                                      disabled={updateRoomRateMut.isPending}
                                    />
                                  ) : (
                                    formatCurrency(row.unitRate)
                                  )}
                                </TableCell>
                                <TableCell className="text-right">{formatCurrency(row.netAmount)}</TableCell>
                                <TableCell>{renderPharmaBilling(row)}</TableCell>
                                {bill.status === 'DRAFT' && (
                                  <TableCell>
                                    <div className="flex items-center gap-1">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8"
                                        onClick={() => {
                                          setEditingItem(row);
                                          setEditItemForm({
                                            description: row.description ?? '',
                                            quantity: String(row.quantity),
                                            unitRate: String(row.unitRate),
                                            serviceDate: row.serviceDate
                                              ? format(new Date(row.serviceDate), "yyyy-MM-dd'T'HH:mm")
                                              : '',
                                          });
                                        }}
                                        aria-label="Edit line item"
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-destructive hover:text-destructive"
                                        onClick={() => setDeleteConfirmItemId(row.id)}
                                        aria-label="Delete line item"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          };

                          const out: JSX.Element[] = [];

                          if (otRows.length > 0) {
                            const net = otRows.reduce((s, r) => s + Number(r.netAmount ?? 0), 0);
                            const qty = otRows.reduce((s, r) => s + Number(r.quantity ?? 0), 0);
                            const dt = [...otRows]
                              .map((r) => r.serviceDate)
                              .filter(Boolean)
                              .sort()[0];
                            const billing = aggBilling(otRows);

                            // Group row
                            out.push(
                              <TableRow key="OT_CONSUMPTION_GROUP" className="bg-slate-50/60">
                                <TableCell>{dt ? formatDate(dt) : '—'}</TableCell>
                                <TableCell>OT_PHARMA</TableCell>
                                <TableCell>
                                  <button
                                    type="button"
                                    className="flex items-center gap-2 hover:underline"
                                    onClick={() => setOtConsumptionExpanded((v) => !v)}
                                  >
                                    {otConsumptionExpanded ? (
                                      <ChevronDown className="h-4 w-4" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4" />
                                    )}
                                    <span className="font-medium">OT Consumption</span>
                                    <span className="text-muted-foreground">({otRows.length})</span>
                                  </button>
                                </TableCell>
                                <TableCell>PHARMA</TableCell>
                                <TableCell className="text-right">{qty}</TableCell>
                                <TableCell className="text-right">
                                  <span className="text-muted-foreground">—</span>
                                </TableCell>
                                <TableCell className="text-right">{formatCurrency(net)}</TableCell>
                                <TableCell>
                                  {renderPharmaBilling(
                                    { sourceType: 'PHARMA', chargeCode: 'OT_PHARMA', description: '', id: 'x', ipdBillId: '', serviceDate: dt || '', quantity: 0, unitRate: 0, grossAmount: 0, gstApplicable: false, netAmount: 0, discountAmount: 0 } as any,
                                    billing
                                  )}
                                </TableCell>
                                {bill.status === 'DRAFT' && <TableCell />}
                              </TableRow>
                            );

                            if (otConsumptionExpanded) {
                              for (const r of otRows) out.push(renderRow(r, { indent: true }));
                            }
                          }

                          for (const r of otherRows) out.push(renderRow(r));

                          return out;
                        })()}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-muted-foreground">No line items. Add chargeable items and run accrual.</p>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Edit line item dialog (DRAFT only) */}
          <Dialog
            open={!!editingItem}
            onOpenChange={(open) => {
              if (!open) setEditingItem(null);
            }}
          >
            <DialogContent className="sm:max-w-md" aria-describedby="edit-item-description">
              <DialogHeader>
                <DialogTitle>Edit line item</DialogTitle>
              </DialogHeader>
              <p id="edit-item-description" className="text-sm text-muted-foreground">
                Change description, quantity, rate, or service date. Gross and net will be recalculated.
              </p>
              {editingItem && (
                <form
                  className="space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!billId || !editingItem) return;
                    const quantity = parseInt(editItemForm.quantity, 10);
                    const unitRate = parseFloat(editItemForm.unitRate);
                    if (isNaN(quantity) || quantity < 0 || isNaN(unitRate) || unitRate < 0) {
                      toast.error('Quantity and rate must be valid numbers');
                      return;
                    }
                    updateBillItemMut.mutate({
                      billId,
                      itemId: editingItem.id,
                      data: {
                        description: editItemForm.description || undefined,
                        quantity,
                        unitRate,
                        // Service date changes for accrued items are not supported (breaks accrual mapping).
                      },
                    });
                  }}
                >
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={editItemForm.description}
                      onChange={(e) => setEditItemForm((f) => ({ ...f, description: e.target.value }))}
                      placeholder="Line item description"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Quantity</Label>
                      <Input
                        type="number"
                        min={0}
                        step={1}
                        value={editItemForm.quantity}
                        onChange={(e) => setEditItemForm((f) => ({ ...f, quantity: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Unit rate (₹)</Label>
                      <Input
                        type="number"
                        min={0}
                        step={0.01}
                        value={editItemForm.unitRate}
                        onChange={(e) => setEditItemForm((f) => ({ ...f, unitRate: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Service date</Label>
                    <Input
                      type="datetime-local"
                      value={editItemForm.serviceDate}
                      onChange={(e) => setEditItemForm((f) => ({ ...f, serviceDate: e.target.value }))}
                      disabled
                    />
                    <p className="text-xs text-muted-foreground">
                      Service date can’t be edited for accrued items. Delete and add a new line if needed.
                    </p>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setEditingItem(null)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={updateBillItemMut.isPending}>
                      {updateBillItemMut.isPending ? 'Saving...' : 'Save'}
                    </Button>
                  </DialogFooter>
                </form>
              )}
            </DialogContent>
          </Dialog>

          {/* Delete line item confirmation */}
          <Dialog
            open={!!deleteConfirmItemId}
            onOpenChange={(open) => {
              if (!open) setDeleteConfirmItemId(null);
            }}
          >
            <DialogContent className="sm:max-w-md" aria-describedby="delete-item-description">
              <DialogHeader>
                <DialogTitle>Delete line item</DialogTitle>
              </DialogHeader>
              <p id="delete-item-description" className="text-sm text-muted-foreground">
                This will remove the line from the bill. Bill totals will be recalculated. This cannot be undone.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDeleteConfirmItemId(null)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  disabled={deleteBillItemMut.isPending}
                  onClick={() => {
                    if (billId && deleteConfirmItemId) {
                      deleteBillItemMut.mutate({ billId, itemId: deleteConfirmItemId });
                    }
                  }}
                >
                  {deleteBillItemMut.isPending ? 'Deleting...' : 'Delete'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ——— Deposits & Balance ——— */}
        <TabsContent value="deposits" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
              <CardTitle>Running balance</CardTitle>
              <div className="flex flex-wrap gap-2">
                {balance && balance.outstanding > 0 && (
                  <Button
                    variant="outline"
                    disabled={billEditBlocked && !canManageDeposits}
                    onClick={() => {
                      setDepositForm({
                        purpose: 'ADVANCE',
                        receiptNumber: '',
                        notes: 'Outstanding balance collection',
                        paymentDate: toIstDatetimeLocalValue(new Date()),
                        amount: '',
                        splits: [
                          {
                            mode: 'CASH',
                            amount: String(balance.outstanding),
                          },
                        ],
                        amountPendingFromThirdParty: false,
                        pendingThirdPartyAmount: '',
                      });
                      setDepositOpen(true);
                    }}
                  >
                    <Wallet className="h-4 w-4 mr-2" />
                    Close outstanding ({formatCurrency(balance.outstanding)})
                  </Button>
                )}
                <Button
                  disabled={billEditBlocked && !canManageDeposits}
                  onClick={() => setDepositOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-2" /> Add deposit
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {canManageDeposits && (
                <p className="text-sm text-muted-foreground mb-3 rounded-md border bg-muted/40 px-3 py-2">
                  {isHospitalAdmin
                    ? 'As hospital admin you can edit or delete deposit rows. Deleting a real payment receipt increases outstanding (and total deposits) immediately. Third-party cheque markers are tracking-only and do not change outstanding. Bill lines are not changed.'
                    : 'For pending third-party collection cases you can edit or delete deposit receipts. Deleting a real payment receipt updates outstanding immediately. When bill net matches total deposits, pending third-party amount clears automatically.'}
                </p>
              )}
              {balance && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                  <div><p className="text-sm text-muted-foreground">Total accrued</p><p className="text-xl font-semibold">{formatCurrency(balance.totalAccrued)}</p></div>
                  <div><p className="text-sm text-muted-foreground">Total deposits</p><p className="text-xl font-semibold text-green-600">{formatCurrency(balance.totalDeposits)}</p></div>
                  {(consolidatedPendingTotal > 0 || (balance.pendingThirdPartyCredit ?? 0) > 0) && (
                    <div>
                      <p className="text-sm text-muted-foreground">Pending third party (consolidated)</p>
                      <p className="text-xl font-semibold text-amber-600">
                        {formatCurrency(
                          Math.max(consolidatedPendingTotal, balance.pendingThirdPartyCredit ?? 0)
                        )}
                      </p>
                    </div>
                  )}
                  <div><p className="text-sm text-muted-foreground">Outstanding</p><p className={`text-xl font-semibold ${balance.outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>{formatCurrency(balance.outstanding)}</p></div>
                  <div><p className="text-sm text-muted-foreground">Excess</p><p className="text-xl">{balance.hasExcess ? 'Yes' : 'No'}</p></div>
                </div>
              )}
              {openPendingCollections.length > 0 && (
                <div className="mb-4 rounded-md border border-amber-200 bg-amber-50/60 p-4 space-y-4">
                  <div>
                    <p className="font-medium text-amber-900">Third-party pending collection (consolidated)</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {openPendingCollections.length} open pending
                      {openPendingCollections.length === 1 ? '' : 's'} totaling{' '}
                      <strong className="text-amber-800">{formatCurrency(consolidatedPendingTotal)}</strong>.
                      Enter one <strong>settled amount</strong> (bank deposit + TDS) against all of them —
                      that amount becomes the collection counted on this IPD.
                    </p>
                  </div>
                  <div className="rounded-md border bg-white/80 p-3 space-y-2 text-sm">
                    {openPendingCollections.map((pc) => (
                      <div key={pc.id} className="flex flex-wrap gap-3 justify-between items-center">
                        <span className="text-muted-foreground">
                          {pc.label || 'Pending'}
                          {pc.financingPartnerName ? ` · ${pc.financingPartnerName}` : ''}
                        </span>
                        <div className="flex items-center gap-2">
                          <strong className="text-amber-800">{formatCurrency(pc.amount)}</strong>
                          {canDeletePendingThirdParty && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 text-destructive hover:text-destructive"
                              disabled={deletePendingThirdPartyMut.isPending}
                              onClick={() => {
                                if (
                                  !window.confirm(
                                    `Delete this pending third-party amount (${formatCurrency(pc.amount)}) completely? This also removes the linked cheque marker receipt.`
                                  )
                                ) {
                                  return;
                                }
                                deletePendingThirdPartyMut.mutate(pc.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    <div className="flex justify-between border-t pt-2 font-semibold">
                      <span>Consolidated pending</span>
                      <span className="text-amber-900">{formatCurrency(consolidatedPendingTotal)}</span>
                    </div>
                  </div>
                  {settlePendingId === 'ALL' ? (
                    <div className="grid gap-3 md:grid-cols-2">
                      <div>
                        <Label>Settled amount (₹) — bank deposit + TDS</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max={consolidatedPendingTotal}
                          value={settleForm.settledAmount}
                          onChange={(e) =>
                            setSettleForm((f) => ({ ...f, settledAmount: e.target.value }))
                          }
                          placeholder={String(consolidatedPendingTotal)}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Must be ≤ consolidated pending ({formatCurrency(consolidatedPendingTotal)}).
                        </p>
                      </div>
                      <div>
                        <Label>Payment mode</Label>
                        <Select
                          value={settleForm.paymentMode}
                          onValueChange={(v: PaymentMethod) =>
                            setSettleForm((f) => ({ ...f, paymentMode: v }))
                          }
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {PAYMENT_METHODS.map((m) => (
                              <SelectItem key={m} value={m}>{m}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Payment date &amp; time (IST)</Label>
                        <Input
                          type="datetime-local"
                          value={settleForm.paymentDate}
                          onChange={(e) =>
                            setSettleForm((f) => ({ ...f, paymentDate: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <Label>Receipt number (optional)</Label>
                        <Input
                          value={settleForm.receiptNumber}
                          onChange={(e) =>
                            setSettleForm((f) => ({ ...f, receiptNumber: e.target.value }))
                          }
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label>Notes (optional)</Label>
                        <Textarea
                          value={settleForm.notes}
                          onChange={(e) =>
                            setSettleForm((f) => ({ ...f, notes: e.target.value }))
                          }
                          placeholder="e.g. TDS ₹X deducted by TPA"
                        />
                      </div>
                      <div className="md:col-span-2 flex gap-2">
                        <Button
                          disabled={collectPendingMut.isPending}
                          onClick={() => {
                            const settled = parseFloat(settleForm.settledAmount);
                            if (!Number.isFinite(settled) || settled <= 0) {
                              toast.error('Enter a valid settled amount');
                              return;
                            }
                            if (settled > consolidatedPendingTotal + 0.009) {
                              toast.error('Settled amount cannot exceed consolidated pending');
                              return;
                            }
                            collectPendingMut.mutate(settled);
                          }}
                        >
                          {collectPendingMut.isPending ? 'Saving…' : 'Record consolidated settlement'}
                        </Button>
                        <Button variant="outline" onClick={() => setSettlePendingId(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      className="border-amber-400"
                      onClick={() => {
                        setSettlePendingId('ALL');
                        setSettleForm({
                          settledAmount: String(consolidatedPendingTotal),
                          paymentMode: 'BANK_TRANSFER',
                          receiptNumber: '',
                          paymentDate: toIstDatetimeLocalValue(new Date()),
                          notes: '',
                        });
                      }}
                    >
                      Enter consolidated settled amount
                    </Button>
                  )}
                </div>
              )}
              {bill?.id && admission?.queue?.patient?.id && balance && balance.outstanding > 0 && (
                <div className="mb-4">
                  <IPDLoyaltyBillingPanel
                    ipdBillId={bill.id}
                    patientId={admission.queue.patient.id}
                    patientName={patientName}
                    outstanding={balance.outstanding}
                    onApplied={() => {
                      queryClient.invalidateQueries({ queryKey: ['ipd-bill-details', admissionId] });
                      queryClient.invalidateQueries({ queryKey: ['ipd-balance', admissionId] });
                    }}
                  />
                </div>
              )}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Purpose</TableHead>
                    <TableHead>Receipt</TableHead>
                    <TableHead>Notes</TableHead>
                    {canManageDeposits && <TableHead className="w-[100px] text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deposits.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell>{formatDate(d.paymentDate)}</TableCell>
                      <TableCell>
                        {d.amountPendingFromThirdParty ? (
                          <span className="tabular-nums">
                            {formatCurrency(d.amount)}{' '}
                            <span className="text-xs text-muted-foreground">(cheque / pending)</span>
                          </span>
                        ) : (
                          formatCurrency(d.amount)
                        )}
                      </TableCell>
                      <TableCell>{d.mode}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1">
                          {d.purpose}
                          {d.amountPendingFromThirdParty && (
                            <Badge variant="outline" className="text-amber-700 border-amber-300">
                              {admission?.financingPartnerName
                                ? `Pending from ${admission.financingPartnerName}`
                                : 'Amount pending from third party'}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {d.receiptNumber ? (
                          <button
                            type="button"
                            className="text-blue-600 hover:underline"
                            onClick={async () => {
                              try {
                                const res = await ipdBillingApi.getDepositReceiptPDF(admissionId!, d.receiptNumber!, billTrack);
                                const blob = new Blob([res.data], { type: 'application/pdf' });
                                const url = window.URL.createObjectURL(blob);
                                window.open(url, '_blank');
                                setTimeout(() => window.URL.revokeObjectURL(url), 60000);
                              } catch (e: any) {
                                toast.error(e?.response?.data?.message || 'Failed to open receipt PDF');
                              }
                            }}
                          >
                            {d.receiptNumber}
                          </button>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{d.notes ?? '—'}</TableCell>
                      {canManageDeposits && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              aria-label="Edit deposit"
                              onClick={() => {
                                setEditingDeposit(d);
                                setDepositForm({
                                  purpose: d.purpose,
                                  receiptNumber: d.receiptNumber ?? '',
                                  notes: d.notes ?? '',
                                  paymentDate: d.paymentDate
                                    ? toIstDatetimeLocalValue(d.paymentDate)
                                    : toIstDatetimeLocalValue(new Date()),
                                  amount: String(d.amount ?? ''),
                                  splits: [{ mode: d.mode, amount: String(d.amount ?? '') }],
                                  amountPendingFromThirdParty: d.amountPendingFromThirdParty ?? false,
                                  pendingThirdPartyAmount: '',
                                });
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              aria-label="Delete deposit"
                              disabled={deleteDepositMut.isPending}
                              onClick={() => {
                                const ok = window.confirm(
                                  'Delete this deposit and its receipt from the system? Bill lines and discharge summary will not be changed.'
                                );
                                if (!ok) return;
                                deleteDepositMut.mutate(d.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ——— Refunds & Settlement ——— */}
        <TabsContent value="refunds" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Settlement at discharge</CardTitle>
              <Button
                variant="outline"
                onClick={() => {
                  setEditingRefund(null);
                  setRefundForm({ amount: '', paymentMode: 'CASH', reason: '' });
                  setRefundOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" /> Add refund
              </Button>
            </CardHeader>
            <CardContent>
              {settlement && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 p-4 bg-muted/50 rounded-lg">
                  <div><p className="text-sm text-muted-foreground">Bill net</p><p className="font-semibold">{formatCurrency(settlement.billNet)}</p></div>
                  <div><p className="text-sm text-muted-foreground">Net deposits</p><p className="font-semibold">{formatCurrency(settlement.netDeposits)}</p></div>
                  <div><p className="text-sm text-muted-foreground">Balance to collect</p><p className={`font-semibold ${settlement.balanceToCollect > 0 ? 'text-red-600' : ''}`}>{formatCurrency(settlement.balanceToCollect)}</p></div>
                  <div><p className="text-sm text-muted-foreground">Refund due</p><p className="font-semibold text-green-600">{formatCurrency(settlement.refundDue)}</p></div>
                </div>
              )}
              <p className="text-sm text-muted-foreground mb-2">Refunds</p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="w-[90px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {refunds.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{formatDate(r.refundDate)}</TableCell>
                      <TableCell>{formatCurrency(r.amount)}</TableCell>
                      <TableCell>{r.paymentMode}</TableCell>
                      <TableCell>{r.reason ?? '—'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            aria-label="Edit refund"
                            onClick={() => {
                              setEditingRefund(r);
                              setRefundForm({
                                amount: String(r.amount ?? ''),
                                paymentMode: r.paymentMode,
                                reason: r.reason ?? '',
                              });
                              setRefundOpen(true);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            aria-label="Delete refund"
                            disabled={deleteRefundMut.isPending}
                            onClick={() => {
                              const ok = window.confirm('Delete this refund entry?');
                              if (!ok) return;
                              deleteRefundMut.mutate(r.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ——— Chargeable items ——— */}
        <TabsContent value="chargeable" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Pharmacy / consumables / misc</CardTitle>
              <Button disabled={billEditBlocked} onClick={() => { setChargeableRows([defaultChargeableRow()]); setAddItemsOpen(true); }}>
                <Plus className="h-4 w-4 mr-2" /> Add items
              </Button>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-2">
                Run accrual on the Bill and Items tab to copy these rows onto the bill. Deleting a bill line no longer
                blocks the same chargeable from accruing again. Stock-linked pharmacy rows: edit description only here, or
                remove via Pharmacist → IPD Pharma.
              </p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Description</TableHead>
                    {showBillSeparatelyColumn ? <TableHead>Bill separately</TableHead> : null}
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    {canEditChargeables ? <TableHead className="w-[100px] text-right">Actions</TableHead> : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {chargeableItems.map((c) => {
                    const lockedPharma =
                      !!c.pharmacyIssue ||
                      (!!c.pharmaBillingStatus && c.pharmaBillingStatus !== 'PENDING');
                    const isMedicineLike = c.sourceType === 'PHARMA' || c.sourceType === 'CONSUMABLE';
                    return (
                      <TableRow key={c.id}>
                        <TableCell>{formatDate(c.serviceDate)}</TableCell>
                        <TableCell>{c.sourceType}</TableCell>
                        <TableCell>{c.chargeCode}</TableCell>
                        <TableCell>{c.description}</TableCell>
                        {showBillSeparatelyColumn ? (
                          <TableCell>
                            {isMedicineLike ? (
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={!!(c as any).billSeparately}
                                  disabled={toggleChargeableBillSeparately.isPending}
                                  onCheckedChange={(v) => {
                                    toggleChargeableBillSeparately.mutate({ id: c.id, billSeparately: v === true });
                                  }}
                                />
                                <span className="text-xs text-muted-foreground">
                                  {(c as any).billSeparately ? 'Bill' : 'Included'}
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        ) : null}
                        <TableCell className="text-right">{c.quantity}</TableCell>
                        <TableCell className="text-right">{formatCurrency(c.unitRate)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(c.quantity * c.unitRate)}</TableCell>
                        {canEditChargeables ? (
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                aria-label="Edit chargeable item"
                                onClick={() => {
                                  setEditingChargeable(c);
                                  setChargeableEditForm({
                                    description: c.description ?? '',
                                    quantity: String(c.quantity),
                                    unitRate: String(c.unitRate),
                                    serviceDate: c.serviceDate
                                      ? format(new Date(c.serviceDate), 'yyyy-MM-dd')
                                      : '',
                                  });
                                }}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                aria-label="Delete chargeable item"
                                disabled={lockedPharma}
                                title={
                                  lockedPharma
                                    ? 'Remove stock-linked pharmacy lines from Pharmacist → IPD Pharma'
                                    : 'Delete this chargeable row'
                                }
                                onClick={() => setDeleteChargeableId(c.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <Dialog
                open={!!editingChargeable}
                onOpenChange={(open) => {
                  if (!open) setEditingChargeable(null);
                }}
              >
                <DialogContent className="sm:max-w-md" aria-describedby="edit-chargeable-desc">
                  <DialogHeader>
                    <DialogTitle>Edit chargeable item</DialogTitle>
                  </DialogHeader>
                  <p id="edit-chargeable-desc" className="text-sm text-muted-foreground">
                    {editingChargeable?.pharmacyIssue
                      ? 'This row is linked to dispensed stock. You can edit the description only; change quantity or rate by reversing the issue in pharmacy.'
                      : 'Update fields, then run accrual to refresh bill line items.'}
                  </p>
                  {editingChargeable && (
                    <form
                      className="space-y-4"
                      onSubmit={(e) => {
                        e.preventDefault();
                        const qty = parseInt(chargeableEditForm.quantity, 10);
                        const rate = parseFloat(chargeableEditForm.unitRate);
                        const payload: {
                          description?: string;
                          quantity?: number;
                          unitRate?: number;
                          serviceDate?: string;
                        } = {
                          description: chargeableEditForm.description.trim(),
                        };
                        if (!editingChargeable.pharmacyIssue) {
                          if (!Number.isNaN(qty) && qty >= 0) payload.quantity = qty;
                          if (!Number.isNaN(rate) && rate >= 0) payload.unitRate = rate;
                          if (chargeableEditForm.serviceDate.trim()) {
                            payload.serviceDate = chargeableEditForm.serviceDate.trim();
                          }
                        }
                        updateChargeableMut.mutate({ id: editingChargeable.id, data: payload });
                      }}
                    >
                      <div className="space-y-2">
                        <Label htmlFor="ch-desc">Description</Label>
                        <Input
                          id="ch-desc"
                          value={chargeableEditForm.description}
                          onChange={(e) => setChargeableEditForm((f) => ({ ...f, description: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="ch-qty">Quantity</Label>
                          <Input
                            id="ch-qty"
                            type="number"
                            min={0}
                            step={1}
                            value={chargeableEditForm.quantity}
                            onChange={(e) => setChargeableEditForm((f) => ({ ...f, quantity: e.target.value }))}
                            disabled={!!editingChargeable.pharmacyIssue}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="ch-rate">Unit rate (₹)</Label>
                          <Input
                            id="ch-rate"
                            type="number"
                            min={0}
                            step="0.01"
                            value={chargeableEditForm.unitRate}
                            onChange={(e) => setChargeableEditForm((f) => ({ ...f, unitRate: e.target.value }))}
                            disabled={!!editingChargeable.pharmacyIssue}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ch-sd">Service date</Label>
                        <Input
                          id="ch-sd"
                          type="date"
                          value={chargeableEditForm.serviceDate}
                          onChange={(e) => setChargeableEditForm((f) => ({ ...f, serviceDate: e.target.value }))}
                          disabled={!!editingChargeable.pharmacyIssue}
                        />
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setEditingChargeable(null)}>
                          Cancel
                        </Button>
                        <Button type="submit" disabled={updateChargeableMut.isPending}>
                          {updateChargeableMut.isPending ? 'Saving…' : 'Save'}
                        </Button>
                      </DialogFooter>
                    </form>
                  )}
                </DialogContent>
              </Dialog>

              <Dialog
                open={!!deleteChargeableId}
                onOpenChange={(open) => {
                  if (!open) setDeleteChargeableId(null);
                }}
              >
                <DialogContent className="sm:max-w-md" aria-describedby="del-chargeable-desc">
                  <DialogHeader>
                    <DialogTitle>Delete chargeable item</DialogTitle>
                  </DialogHeader>
                  <p id="del-chargeable-desc" className="text-sm text-muted-foreground">
                    This removes the row from the chargeable list and clears any bill rules tied to it. Run accrual
                    afterwards to update the IPD bill.
                  </p>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDeleteChargeableId(null)}>
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      disabled={deleteChargeableMut.isPending || !deleteChargeableId}
                      onClick={() => {
                        if (deleteChargeableId) deleteChargeableMut.mutate(deleteChargeableId);
                      }}
                    >
                      {deleteChargeableMut.isPending ? 'Deleting…' : 'Delete'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ——— Insurance & Claim ——— */}
        <TabsContent value="insurance" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Insurance case & claim view</CardTitle>
              {!insuranceCase ? (
                <Button onClick={() => setInsuranceCaseOpen(true)}><Plus className="h-4 w-4 mr-2" /> Create insurance case</Button>
              ) : (
                <Button variant="outline" onClick={() => setInsuranceCaseOpen(true)}>Edit case</Button>
              )}
            </CardHeader>
            <CardContent>
              {insuranceCase ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div><p className="text-sm text-muted-foreground">Payer</p><p className="font-medium">{insuranceCase.payerName}</p></div>
                    <div><p className="text-sm text-muted-foreground">Policy</p><p className="font-medium">{insuranceCase.policyNumber}</p></div>
                    <div><p className="text-sm text-muted-foreground">TPA</p><p className="font-medium">{insuranceCase.tpaName ?? '—'}</p></div>
                    <div><p className="text-sm text-muted-foreground">Verified</p><p className="font-medium">{insuranceCase.verifiedAt ? formatDate(insuranceCase.verifiedAt) : 'No'}</p></div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => runAdjudicationMut.mutate()} disabled={runAdjudicationMut.isPending || !bill?.billItems?.length}>
                      {runAdjudicationMut.isPending ? 'Running...' : 'Run adjudication'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={async () => {
                        try {
                          const res = await ipdBillingApi.getInsuranceClaimView(admissionId!, billTrack);
                          setClaimView(res.data?.data ?? null);
                        } catch {
                          setClaimView(null);
                        }
                      }}
                    >
                      View claim
                    </Button>
                  </div>
                  {claimView && (
                    <div className="mt-4 p-4 border rounded-lg">
                      <h4 className="font-medium mb-2">Claim summary</h4>
                      <p>Total admissible: {formatCurrency(claimView.totalAdmissible)} · Total non-payable: {formatCurrency(claimView.totalNonPayable)}</p>
                      {claimView.items?.length > 0 && (
                        <Table className="mt-2">
                          <TableHeader>
                            <TableRow>
                              <TableHead>Description</TableHead>
                              <TableHead className="text-right">Actual</TableHead>
                              <TableHead className="text-right">Admissible</TableHead>
                              <TableHead className="text-right">Non-payable</TableHead>
                              <TableHead>Reason</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {claimView.items.map((item: any, i: number) => (
                              <TableRow key={i}>
                                <TableCell>{item.billItem?.description}</TableCell>
                                <TableCell className="text-right">{formatCurrency(item.actualAmount)}</TableCell>
                                <TableCell className="text-right">{formatCurrency(item.admissibleAmount)}</TableCell>
                                <TableCell className="text-right">{formatCurrency(item.nonPayableAmount)}</TableCell>
                                <TableCell>{item.reasonCode ?? '—'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-muted-foreground">Create an insurance case to run adjudication and view the claim.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ——— Notes & Audit ——— */}
        <TabsContent value="notes" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Credit/Debit notes & audit log</CardTitle>
              {bill?.status === 'FROZEN' && (
                <Button onClick={() => setNoteOpen(true)}><Plus className="h-4 w-4 mr-2" /> Add note</Button>
              )}
            </CardHeader>
            <CardContent>
              {bill?.status !== 'FROZEN' && (
                <p className="text-amber-600 text-sm mb-4">Notes can be added only when the bill is FROZEN.</p>
              )}
              <h4 className="font-medium mb-2">Notes</h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {notes.map((n) => (
                    <TableRow key={n.id}>
                      <TableCell>{formatDate(n.createdAt)}</TableCell>
                      <TableCell>{n.noteType}</TableCell>
                      <TableCell>{formatCurrency(n.amount)}</TableCell>
                      <TableCell>{n.reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <h4 className="font-medium mt-6 mb-2">Audit log</h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>From → To</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLogs.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{formatDate(a.createdAt)}</TableCell>
                      <TableCell>{a.action}</TableCell>
                      <TableCell>{a.previousStatus ?? '—'} → {a.newStatus ?? '—'}</TableCell>
                      <TableCell>{a.reason ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog
        open={pharmaReceptionOpen}
        onOpenChange={(o) => {
          setPharmaReceptionOpen(o);
          if (!o) setPharmaReceptionRow(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Collect pharma line at reception</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{pharmaReceptionRow?.description ?? 'Line'}</span>
              {' — '}
              {pharmaReceptionRow ? formatCurrency(pharmaReceptionRow.netAmount) : '—'}
            </p>
            <div className="space-y-2">
              <Label>Payment mode</Label>
              <Select value={pharmaReceptionMode} onValueChange={(v) => setPharmaReceptionMode(v as PaymentMethod)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m.replace(/_/g, ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                This amount is already included in the IPD bill net. Confirming only marks the line as collected at
                reception — it does not change the running balance. Record payment under Deposits if still due.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" type="button" onClick={() => setPharmaReceptionOpen(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={settlePharmaReceptionMut.isPending} onClick={() => settlePharmaReceptionMut.mutate()}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit deposit dialog (hospital admin) */}
      <Dialog open={!!editingDeposit} onOpenChange={(o) => !o && setEditingDeposit(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit deposit</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Amount</Label>
              <Input
                type="number"
                step="0.01"
                className="mt-1"
                value={depositForm.splits[0]?.amount ?? ''}
                onChange={(e) =>
                  setDepositForm((f) => ({
                    ...f,
                    splits: [{ ...f.splits[0], amount: e.target.value }],
                  }))
                }
              />
            </div>
            <div>
              <Label>Mode</Label>
              <Select
                value={depositForm.splits[0]?.mode ?? 'CASH'}
                onValueChange={(v: PaymentMethod) =>
                  setDepositForm((f) => ({
                    ...f,
                    splits: [{ ...f.splits[0], mode: v }],
                  }))
                }
              >
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Purpose</Label>
              <Select
                value={depositForm.purpose}
                onValueChange={(v: IPDDepositPurpose) => setDepositForm((f) => ({ ...f, purpose: v }))}
              >
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DEPOSIT_PURPOSES.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment date &amp; time (IST)</Label>
              <Input
                type="datetime-local"
                className="mt-1"
                value={depositForm.paymentDate}
                onChange={(e) => setDepositForm((f) => ({ ...f, paymentDate: e.target.value }))}
              />
            </div>
            <div>
              <Label>Receipt number</Label>
              <Input
                className="mt-1"
                value={depositForm.receiptNumber}
                onChange={(e) => setDepositForm((f) => ({ ...f, receiptNumber: e.target.value }))}
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                className="mt-1"
                value={depositForm.notes}
                onChange={(e) => setDepositForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingDeposit(null)}>Cancel</Button>
            <Button
              disabled={updateDepositMut.isPending}
              onClick={() => {
                if (!editingDeposit) return;
                const amt = parseFloat(depositForm.splits[0]?.amount ?? '');
                if (!Number.isFinite(amt) || amt <= 0) {
                  toast.error('Enter a valid amount');
                  return;
                }
                updateDepositMut.mutate({
                  depositId: editingDeposit.id,
                  amount: amt,
                  mode: depositForm.splits[0]?.mode ?? 'CASH',
                  purpose: depositForm.purpose,
                  paymentDate: depositForm.paymentDate,
                  receiptNumber: depositForm.receiptNumber.trim() || null,
                  notes: depositForm.notes.trim() || null,
                });
              }}
            >
              {updateDepositMut.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add deposit dialog */}
      <Dialog open={depositOpen} onOpenChange={setDepositOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add deposit</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              {!depositForm.amountPendingFromThirdParty && (
              <>
              <div className="flex items-center justify-between gap-3">
                <Label>Payment modes</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setDepositForm((f) => ({
                      ...f,
                      splits: [...f.splits, { mode: 'CASH' as PaymentMethod, amount: '' }]
                    }))
                  }
                >
                  + Add mode
                </Button>
              </div>

              {depositForm.splits.map((s, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-7">
                    <Label className="mb-1">Mode</Label>
                    <Select
                      value={s.mode}
                      onValueChange={(v: PaymentMethod) =>
                        setDepositForm((f) => ({
                          ...f,
                          splits: f.splits.map((x, i) => (i === idx ? { ...x, mode: v } : x))
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_METHODS.map((m) => (
                          <SelectItem key={m} value={m}>
                            {m}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="col-span-4">
                    <Label className="mb-1">Amount</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={s.amount}
                      onChange={(e) =>
                        setDepositForm((f) => ({
                          ...f,
                          splits: f.splits.map((x, i) => (i === idx ? { ...x, amount: e.target.value } : x))
                        }))
                      }
                    />
                  </div>

                  <div className="col-span-1">
                    {depositForm.splits.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setDepositForm((f) => ({
                            ...f,
                            splits: f.splits.filter((_, i) => i !== idx)
                          }))
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}

              <div className="pt-2 border-t">
                <p className="text-sm text-muted-foreground">
                  Total:{" "}
                  <strong className="text-foreground">
                    ₹
                    {depositForm.splits.reduce((sum, sp) => {
                      const n = parseFloat(sp.amount);
                      return sum + (Number.isFinite(n) ? n : 0);
                    }, 0).toFixed(2)}
                  </strong>
                </p>
              </div>
              </>
              )}
            </div>
            <div><Label>Purpose</Label>
              <Select
                value={depositForm.purpose}
                onValueChange={(v: IPDDepositPurpose) =>
                  setDepositForm((f) => ({
                    ...f,
                    purpose: v,
                    amountPendingFromThirdParty:
                      v === 'ADVANCE' || v === 'TOP_UP' || v === 'FULL_AND_FINAL'
                        ? f.amountPendingFromThirdParty
                        : false,
                    pendingThirdPartyAmount:
                      v === 'ADVANCE' || v === 'TOP_UP' || v === 'FULL_AND_FINAL'
                        ? f.pendingThirdPartyAmount
                        : '',
                  }))
                }
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DEPOSIT_PURPOSES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {(depositForm.purpose === 'FULL_AND_FINAL' ||
              depositForm.purpose === 'ADVANCE' ||
              depositForm.purpose === 'TOP_UP') && (
              <div className="rounded-md border bg-amber-50/50 dark:bg-amber-950/20 p-3 space-y-3">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="third-party-pending"
                    checked={depositForm.amountPendingFromThirdParty}
                    onCheckedChange={(checked) =>
                      setDepositForm((f) => ({
                        ...f,
                        amountPendingFromThirdParty: checked === true,
                        splits: checked === true ? [{ mode: 'CASH' as PaymentMethod, amount: '' }] : f.splits,
                        pendingThirdPartyAmount:
                          checked === true && !f.pendingThirdPartyAmount
                            ? String(
                                Math.max(
                                  0,
                                  balance?.outstandingGross ?? balance?.outstanding ?? 0
                                )
                              )
                            : f.pendingThirdPartyAmount,
                      }))
                    }
                  />
                  <div className="space-y-1">
                    <Label htmlFor="third-party-pending" className="cursor-pointer">
                      Amount pending from third party
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Use when money is not yet received at reception (e.g. insurance / TPA / CareFi), for
                      Advance, Top Up, or Full &amp; Final. Creates a tracking <strong>cheque</strong> marker
                      with a system receipt number (excluded from final bill payment details). The amount
                      covers desk <strong>Outstanding</strong> until settled under IPD pending collection.
                    </p>
                  </div>
                </div>
                {depositForm.amountPendingFromThirdParty && admission?.financingPartnerName ? (
                  <div className="rounded-md border border-amber-200 bg-white/80 px-3 py-2">
                    <p className="text-sm font-semibold text-amber-900">
                      Financing partner: {admission.financingPartnerName}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      This name will appear on the pending collection record.
                    </p>
                  </div>
                ) : null}
                {depositForm.amountPendingFromThirdParty && (
                  <div>
                    <Label>Pending amount (₹)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={depositForm.pendingThirdPartyAmount}
                      onChange={(e) =>
                        setDepositForm((f) => ({ ...f, pendingThirdPartyAmount: e.target.value }))
                      }
                    />
                  </div>
                )}
              </div>
            )}
            <div>
              <Label>Payment date &amp; time (IST)</Label>
              <Input
                type="datetime-local"
                value={depositForm.paymentDate}
                onChange={(e) => setDepositForm((f) => ({ ...f, paymentDate: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground mt-1">Defaults to now; adjust for back-dated deposits. Does not change bill accrual lines.</p>
            </div>
            <div>
              <Label>Receipt number (optional)</Label>
              <Input
                value={depositForm.receiptNumber}
                onChange={(e) => setDepositForm((f) => ({ ...f, receiptNumber: e.target.value }))}
                placeholder="Leave blank to auto-generate IPD-REC-…"
              />
              {depositForm.amountPendingFromThirdParty && (
                <p className="text-xs text-muted-foreground mt-1">
                  Leave blank for third-party markers — system assigns a proper receipt number.
                </p>
              )}
            </div>
            <div><Label>Notes (optional)</Label><Textarea value={depositForm.notes} onChange={(e) => setDepositForm((f) => ({ ...f, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDepositOpen(false)}>Cancel</Button>
            <Button onClick={() => {
              const thirdPartyPending = depositForm.amountPendingFromThirdParty;
              const pendingAmt = parseFloat(depositForm.pendingThirdPartyAmount);
              if (thirdPartyPending && (!Number.isFinite(pendingAmt) || pendingAmt <= 0)) {
                toast.error('Enter a valid pending third-party amount');
                return;
              }
              if (!thirdPartyPending) {
                const parsedSplits = depositForm.splits
                  .map((sp) => ({
                    mode: sp.mode,
                    amount: parseFloat(sp.amount),
                  }))
                  .filter((s) => Number.isFinite(s.amount) && s.amount > 0);
                if (parsedSplits.length === 0) {
                  toast.error('Enter at least one payment amount or mark third-party pending');
                  return;
                }
                if (parsedSplits.some((s) => !s.amount || s.amount <= 0)) {
                  toast.error('Enter valid amount for each payment mode');
                  return;
                }
                const total = parsedSplits.reduce((sum, s) => sum + s.amount, 0);
                if (total <= 0) {
                  toast.error('Total amount must be greater than 0');
                  return;
                }
                addDepositMut.mutate({
                  amount: total,
                  splits: parsedSplits,
                  purpose: depositForm.purpose,
                  receiptNumber: depositForm.receiptNumber || undefined,
                  paymentDate: depositForm.paymentDate
                    ? istDatetimeLocalToIso(depositForm.paymentDate)
                    : undefined,
                  notes: depositForm.notes || undefined,
                });
                return;
              }
              addDepositMut.mutate({
                purpose: depositForm.purpose,
                receiptNumber: depositForm.receiptNumber || undefined,
                paymentDate: depositForm.paymentDate
                  ? istDatetimeLocalToIso(depositForm.paymentDate)
                  : undefined,
                notes: depositForm.notes || undefined,
                amountPendingFromThirdParty: thirdPartyPending || undefined,
                pendingThirdPartyAmount: thirdPartyPending ? pendingAmt : undefined,
              });
            }} disabled={addDepositMut.isPending}>
              {addDepositMut.isPending ? 'Saving…' : 'Add deposit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add refund dialog */}
      <Dialog
        open={refundOpen}
        onOpenChange={(o) => {
          setRefundOpen(o);
          if (!o) {
            setEditingRefund(null);
            setRefundForm({ amount: '', paymentMode: 'CASH', reason: '' });
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingRefund ? 'Edit refund' : 'Add refund'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Amount</Label><Input type="number" step="0.01" value={refundForm.amount} onChange={(e) => setRefundForm((f) => ({ ...f, amount: e.target.value }))} /></div>
            <div><Label>Payment mode</Label>
              <Select value={refundForm.paymentMode} onValueChange={(v: PaymentMethod) => setRefundForm((f) => ({ ...f, paymentMode: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Reason (optional)</Label><Textarea value={refundForm.reason} onChange={(e) => setRefundForm((f) => ({ ...f, reason: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundOpen(false)}>Cancel</Button>
            <Button onClick={() => {
              const amt = parseFloat(refundForm.amount);
              if (isNaN(amt) || amt <= 0) { toast.error('Enter valid amount'); return; }
              if (editingRefund) {
                updateRefundMut.mutate({
                  refundId: editingRefund.id,
                  amount: amt,
                  paymentMode: refundForm.paymentMode,
                  reason: refundForm.reason || undefined,
                });
              } else {
                addRefundMut.mutate({ amount: amt, paymentMode: refundForm.paymentMode, reason: refundForm.reason || undefined });
              }
            }}
            disabled={addRefundMut.isPending || updateRefundMut.isPending}
          >
            {editingRefund ? 'Save changes' : 'Add refund'}
          </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add items / consultant – single dialog, multiple rows */}
      <Dialog
        open={addItemsOpen}
        onOpenChange={(open) => {
          setAddItemsOpen(open);
          if (!open) setLabTestSearchByRow({});
        }}
      >
        <DialogContent
          className="max-w-4xl max-h-[90vh] overflow-y-auto"
          data-enter-next-scope="true"
          onKeyDownCapture={handleEnterToNextField}
        >
          <DialogHeader>
            <DialogTitle>Add items & consultants</DialogTitle>
            <p className="text-sm text-muted-foreground">Add pharmacy, consumables, consultants or other charges. Each row is one line item. Run accrual to include in bill.</p>
          </DialogHeader>
          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-2 text-sm font-medium text-muted-foreground px-1">
              <div className="col-span-2">Type</div>
              <div className="col-span-2">Code / Lab tests</div>
              <div className="col-span-3">Description</div>
              <div className="col-span-1 text-right">Qty</div>
              <div className="col-span-2 text-right">Rate (₹)</div>
              <div className="col-span-1">Date</div>
              <div className="col-span-1" />
            </div>
            {chargeableRows.map((row, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-2">
                  <Select
                    value={row.type}
                    onValueChange={(v: ChargeableRowType) =>
                      setChargeableRows((prev) => prev.map((r, i) => (i === index ? { ...r, type: v } : r)))
                    }
                  >
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CHARGEABLE_ROW_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                      {customChargeableTypes.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  {row.type === 'LAB_TEST' ? (
                    <Popover
                      onOpenChange={(open) => {
                        if (!open) {
                          setLabTestSearchByRow((prev) => {
                            const next = { ...prev };
                            delete next[index];
                            return next;
                          });
                        }
                      }}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="h-9 w-full justify-start text-left font-normal"
                        >
                          {row.labTestIds && row.labTestIds.length > 0
                            ? `${row.labTestIds.length} test(s) selected`
                            : 'Select lab tests'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-0" align="start">
                        {labTestsMaster.length === 0 ? (
                          <p className="text-sm text-muted-foreground px-3 py-2">No lab tests configured.</p>
                        ) : (
                          <>
                            <div className="border-b p-2">
                              <Input
                                placeholder="Search lab tests…"
                                value={labTestSearchByRow[index] ?? ''}
                                onChange={(e) =>
                                  setLabTestSearchByRow((prev) => ({ ...prev, [index]: e.target.value }))
                                }
                                autoFocus
                                onKeyDown={(e) => e.stopPropagation()}
                              />
                            </div>
                            <div className="max-h-64 overflow-y-auto p-1">
                              {(() => {
                                const filteredTests = filterLabTestsForSearch(
                                  labTestsMaster,
                                  labTestSearchByRow[index] ?? '',
                                );
                                if (filteredTests.length === 0) {
                                  return (
                                    <p className="text-sm text-muted-foreground px-2 py-3">No lab tests found.</p>
                                  );
                                }
                                return filteredTests.map((test) => {
                                  const checked = row.labTestIds?.includes(test.id) ?? false;
                                  return (
                                    <label
                                      key={test.id}
                                      className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-gray-50 rounded"
                                    >
                                      <Checkbox
                                        checked={checked}
                                        onCheckedChange={(val) => {
                                          setChargeableRows((prev) =>
                                            prev.map((r, i) => {
                                              if (i !== index) return r;
                                              const current = r.labTestIds ?? [];
                                              if (val) {
                                                return {
                                                  ...r,
                                                  labTestIds: current.includes(test.id)
                                                    ? current
                                                    : [...current, test.id],
                                                };
                                              }
                                              return {
                                                ...r,
                                                labTestIds: current.filter((id) => id !== test.id),
                                              };
                                            }),
                                          );
                                        }}
                                      />
                                      <div className="flex flex-col min-w-0">
                                        <span className="font-medium truncate">{test.name}</span>
                                        <span className="text-xs text-muted-foreground">
                                          {test.code} · ₹{test.charge}
                                        </span>
                                      </div>
                                    </label>
                                  );
                                });
                              })()}
                            </div>
                          </>
                        )}
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <Input
                      className="h-9"
                      placeholder={row.type === 'PHARMA' || row.type === 'CONSUMABLE' ? 'e.g. MED_001' : '—'}
                      value={row.type === 'PHARMA' || row.type === 'CONSUMABLE' ? row.chargeCode : ''}
                      onChange={(e) =>
                        setChargeableRows((prev) =>
                          prev.map((r, i) => (i === index ? { ...r, chargeCode: e.target.value } : r)),
                        )
                      }
                      disabled={row.type !== 'PHARMA' && row.type !== 'CONSUMABLE'}
                    />
                  )}
                </div>
                <div className="col-span-3">
                  {row.type === 'CONSULTANT_VISIT' || row.type === 'ANESTHETIST' || row.type === 'SURGEON_FEES' ? (
                    <div className="flex items-center gap-2">
                      <SearchableSelect
                        value={row.doctorId || '__none__'}
                        onValueChange={(v) => {
                          const id = v === '__none__' ? undefined : v;
                          const doc = id ? doctors.find((d) => d.id === id) : undefined;
                          setChargeableRows((prev) =>
                            prev.map((r, i) =>
                              i === index
                                ? {
                                    ...r,
                                    doctorId: id,
                                    description: doc ? ensureDoctorPrefix(doc.name) : r.description,
                                  }
                                : r
                            )
                          );
                        }}
                        placeholder="Select doctor"
                        searchPlaceholder="Type doctor name…"
                        triggerClassName="h-9 w-full"
                        options={[
                          { value: '__none__', label: 'Select doctor' },
                          ...doctors.map((d) => ({
                            value: d.id,
                            label: ensureDoctorPrefix(d.name),
                            searchText: [d.name, d.specialisation].filter(Boolean).join(' '),
                          })),
                        ]}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="h-9 whitespace-nowrap"
                        onClick={() => {
                          setAddDoctorRowIndex(index);
                          setAddDoctorOpen(true);
                        }}
                      >
                        + Add
                      </Button>
                    </div>
                  ) : (
                    <Input
                      className="h-9"
                      placeholder={row.type === 'LAB_TEST' ? 'Auto from selected lab tests' : 'Description'}
                      value={row.description}
                      onChange={(e) =>
                        setChargeableRows((prev) =>
                          prev.map((r, i) => (i === index ? { ...r, description: e.target.value } : r)),
                        )
                      }
                      disabled={row.type === 'LAB_TEST'}
                    />
                  )}
                </div>
                <div className="col-span-1">
                  <Input
                    type="number"
                    min={1}
                    className="h-9 text-right"
                    value={row.quantity}
                    onChange={(e) =>
                      setChargeableRows((prev) =>
                        prev.map((r, i) => (i === index ? { ...r, quantity: e.target.value } : r)),
                      )
                    }
                    disabled={row.type === 'LAB_TEST'}
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    className="h-9 text-right"
                    placeholder={row.type === 'LAB_TEST' ? 'Auto from test rate' : '0'}
                    value={row.unitRate}
                    onChange={(e) =>
                      setChargeableRows((prev) =>
                        prev.map((r, i) => (i === index ? { ...r, unitRate: e.target.value } : r)),
                      )
                    }
                    disabled={row.type === 'LAB_TEST'}
                  />
                </div>
                <div className="col-span-1">
                  <Popover open={openDateRowIndex === index} onOpenChange={(open) => setOpenDateRowIndex(open ? index : null)}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn('h-9 w-full justify-start text-left font-normal', !row.serviceDate && 'text-muted-foreground')}
                      >
                        <CalendarIcon className="mr-1.5 h-4 w-4 shrink-0" />
                        {row.serviceDate ? format(new Date(row.serviceDate + 'T12:00:00'), 'dd/MM/yyyy') : 'Pick date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={row.serviceDate ? new Date(row.serviceDate + 'T12:00:00') : undefined}
                        onSelect={(date) => {
                          if (date) {
                            setChargeableRows((prev) => prev.map((r, i) => (i === index ? { ...r, serviceDate: format(date, 'yyyy-MM-dd') } : r)));
                            setOpenDateRowIndex(null);
                          }
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="col-span-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 text-muted-foreground hover:text-destructive"
                    onClick={() => setChargeableRows((prev) => prev.filter((_, i) => i !== index))}
                    disabled={chargeableRows.length <= 1}
                  >
                    ×
                  </Button>
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => setChargeableRows((prev) => [...prev, defaultChargeableRow()])}
            >
              <Plus className="h-4 w-4 mr-1" /> Add row
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddItemsOpen(false)}>Cancel</Button>
            <Button
              disabled={addItemsSubmitting}
              onClick={async () => {
                const toAdd: { sourceType: IPDChargeSourceType; chargeCode: string; description: string; quantity: number; unitRate: number; serviceDate?: string }[] = [];
                const newlyCreatedCustomTypes: { value: ChargeableRowType; label: string }[] = [];
                for (const row of chargeableRows) {
                  const { sourceType, chargeCode: codeFromType } = rowTypeToSourceAndCode(row.type);
                  const label = CHARGEABLE_ROW_TYPES.find((t) => t.value === row.type)?.label ?? row.type;

                  if (row.type === 'LAB_TEST') {
                    const selectedIds = row.labTestIds ?? [];
                    if (selectedIds.length === 0) continue;
                    for (const id of selectedIds) {
                      const test = labTestsMaster.find((t) => t.id === id);
                      if (!test) continue;
                      const rate = Number(test.charge) || 0;
                      if (rate <= 0) continue;
                      toAdd.push({
                        sourceType: 'MISC',
                        chargeCode: test.code || 'LAB_TEST',
                        description: test.name || label,
                        quantity: 1,
                        unitRate: rate,
                        serviceDate: row.serviceDate || undefined,
                      });
                    }
                    continue;
                  }

                  const qty = parseInt(row.quantity, 10);
                  const rate = parseFloat(row.unitRate);
                  if (isNaN(rate) || rate < 0) continue;
                  const chargeCode = sourceType === 'MISC' ? row.type : (row.chargeCode.trim() || codeFromType || row.type);
                  if (sourceType === 'MISC' && row.type !== 'OTHER' && !row.description.trim()) continue; // consultant/anesthetist need a name

                  let description = row.description.trim()
                    ? (sourceType === 'MISC' && row.type !== 'OTHER' ? `${row.description.trim()} - ${label}` : row.description.trim())
                    : label;

                  // When user picks "Other" and enters a description, convert it into a reusable custom type
                  if (row.type === 'OTHER' && row.description.trim()) {
                    const customLabel = row.description.trim();
                    const slug = customLabel.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                    const customValue: ChargeableRowType = (`CUSTOM_${slug || Date.now()}` as ChargeableRowType);
                    description = customLabel;
                    newlyCreatedCustomTypes.push({ value: customValue, label: customLabel });
                  }

                  if (!description) continue;
                  const quantity = sourceType === 'MISC' ? 1 : (isNaN(qty) || qty < 1 ? 1 : qty);
                  toAdd.push({
                    sourceType,
                    chargeCode: chargeCode || row.type,
                    description,
                    quantity,
                    unitRate: rate,
                    serviceDate: row.serviceDate || undefined,
                  });
                }
                if (toAdd.length === 0) {
                  toast.error('Fill at least one row with rate ≥ 0 and description');
                  return;
                }
                setAddItemsSubmitting(true);
                try {
                  for (const item of toAdd) {
                    await ipdBillingApi.addChargeableItem(admissionId!, item);
                  }
                  queryClient.invalidateQueries({ queryKey: ['ipd-chargeable', admissionId] });
                  // Persist any new custom types created from "Other" descriptions
                  if (newlyCreatedCustomTypes.length > 0) {
                    setCustomChargeableTypes((prev) => {
                      const existingValues = new Set(prev.map((t) => t.value));
                      const merged = [
                        ...prev,
                        ...newlyCreatedCustomTypes.filter((t) => !existingValues.has(t.value)),
                      ];
                      try {
                        window.localStorage.setItem('ipd-chargeable-custom-types', JSON.stringify(merged));
                      } catch {
                        // ignore
                      }
                      return merged;
                    });
                  }
                  setAddItemsOpen(false);
                  setChargeableRows([defaultChargeableRow()]);
                  toast.success(`${toAdd.length} item(s) added. Run accrual to include in bill.`);
                } catch (e: any) {
                  toast.error(e.response?.data?.message || 'Failed to add items');
                } finally {
                  setAddItemsSubmitting(false);
                }
              }}
            >
              {addItemsSubmitting ? 'Adding...' : 'Add all'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={addDoctorOpen}
        onOpenChange={(open) => {
          setAddDoctorOpen(open);
          if (!open) {
            setNewDoctorName('');
            setNewDoctorSpec('');
            setAddDoctorRowIndex(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add doctor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Doctor name</Label>
              <Input
                value={newDoctorName}
                onChange={(e) => setNewDoctorName(e.target.value)}
                placeholder="e.g. Dr. Sharma"
              />
            </div>
            <div>
              <Label>Specialisation</Label>
              <Select value={newDoctorSpec || '__none__'} onValueChange={(v) => setNewDoctorSpec(v === '__none__' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select specialisation" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__none__">Not specified</SelectItem>
                  {MEDICAL_SPECIALISATIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDoctorOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleQuickCreateDoctor} disabled={creatingDoctor}>
              {creatingDoctor ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Insurance case dialog */}
      <Dialog open={insuranceCaseOpen} onOpenChange={setInsuranceCaseOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{insuranceCase ? 'Edit insurance case' : 'Create insurance case'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Payer name *</Label><Input value={insuranceForm.payerName} onChange={(e) => setInsuranceForm((f) => ({ ...f, payerName: e.target.value }))} /></div>
            <div><Label>Policy number *</Label><Input value={insuranceForm.policyNumber} onChange={(e) => setInsuranceForm((f) => ({ ...f, policyNumber: e.target.value }))} /></div>
            <div><Label>TPA name (optional)</Label><Input value={insuranceForm.tpaName} onChange={(e) => setInsuranceForm((f) => ({ ...f, tpaName: e.target.value }))} /></div>
            <div><Label>Coverage type (optional)</Label><Input value={insuranceForm.coverageType} onChange={(e) => setInsuranceForm((f) => ({ ...f, coverageType: e.target.value }))} /></div>
            <div><Label>Sum insured (optional)</Label><Input type="number" step="0.01" value={insuranceForm.sumInsured} onChange={(e) => setInsuranceForm((f) => ({ ...f, sumInsured: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInsuranceCaseOpen(false)}>Cancel</Button>
            <Button onClick={() => {
              if (!insuranceForm.payerName.trim() || !insuranceForm.policyNumber.trim()) { toast.error('Payer name and policy number required'); return; }
              const sumInsured = insuranceForm.sumInsured ? parseFloat(insuranceForm.sumInsured) : undefined;
              createOrGetInsuranceMut.mutate({
                payerName: insuranceForm.payerName.trim(),
                policyNumber: insuranceForm.policyNumber.trim(),
                tpaName: insuranceForm.tpaName.trim() || undefined,
                coverageType: insuranceForm.coverageType.trim() || undefined,
                sumInsured,
              });
            }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit/Debit note dialog */}
      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add credit/debit note</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Type</Label>
              <Select value={noteForm.noteType} onValueChange={(v: 'CREDIT' | 'DEBIT') => setNoteForm((f) => ({ ...f, noteType: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="CREDIT">CREDIT</SelectItem><SelectItem value="DEBIT">DEBIT</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Amount</Label><Input type="number" step="0.01" value={noteForm.amount} onChange={(e) => setNoteForm((f) => ({ ...f, amount: e.target.value }))} /></div>
            <div><Label>Reason *</Label><Textarea value={noteForm.reason} onChange={(e) => setNoteForm((f) => ({ ...f, reason: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteOpen(false)}>Cancel</Button>
            <Button onClick={() => {
              const amt = parseFloat(noteForm.amount);
              if (isNaN(amt) || amt <= 0 || !noteForm.reason.trim()) { toast.error('Valid amount and reason required'); return; }
              createNoteMut.mutate({ noteType: noteForm.noteType, amount: amt, reason: noteForm.reason.trim() });
            }}>Create note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={billEditRequestOpen} onOpenChange={setBillEditRequestOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request admin approval to edit bill</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            After discharge, revenue bill changes need hospital admin approval. Describe why you
            need to modify the bill (minimum 10 characters).
          </p>
          <Textarea
            value={billEditRequestReason}
            onChange={(e) => setBillEditRequestReason(e.target.value)}
            rows={4}
            placeholder="e.g. Additional pharmacy charges missed at discharge…"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setBillEditRequestOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={
                billEditRequestMut.isPending || billEditRequestReason.trim().length < 10
              }
              onClick={() => billEditRequestMut.mutate(billEditRequestReason.trim())}
            >
              {billEditRequestMut.isPending ? 'Submitting…' : 'Submit request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
